import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { BRAZIL_STATES, BRAZIL_REGIONS } from "@/lib/brazil";
import { toast } from "sonner";
import {
  Plus, Pencil, Trash2, FileText, Users as UsersIcon,
  MessageSquare, LayoutDashboard, Search, Radio, Heart,
  LifeBuoy, Phone, CheckCircle2, Clock, Send,
  FileDown, HeartHandshake, Download, ToggleLeft, ToggleRight,
  Eye, Image as ImageIcon, Upload, CalendarClock, BookOpenCheck, Sparkles,
  GraduationCap, PlayCircle, ListChecks, Youtube, Stethoscope,
  MessageCircle, Bot, User, RefreshCw, Users, ShoppingBag, Package, ClipboardList, Handshake,
} from "lucide-react";
import { useRef } from "react";
import AuthImage from "@/components/AuthImage";
import { useAppConfig } from "@/context/AppConfigContext";

export default function Admin() {
  const { user } = useAuth();
  const [tab, setTab] = useState("dashboard");

  return (
    <div className="container-cuidar py-12">
      <div className="mb-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-xs font-semibold tracking-widest uppercase text-[hsl(var(--accent))]">Área restrita</div>
          <h1 className="mt-2 font-heading text-4xl font-black">Painel do Instituto</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">Olá, {user?.name}. Gerencie postagens, cadastros e contatos.</p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="space-y-6">
        <TabsList className="rounded-full bg-[hsl(var(--muted))] p-1.5 h-auto flex-wrap">
          <TabsTrigger value="dashboard" className="rounded-full px-4 py-2 gap-1.5" data-testid="admin-tab-dashboard"><LayoutDashboard className="h-4 w-4" /> Visão geral</TabsTrigger>
          <TabsTrigger value="care" className="rounded-full px-4 py-2 gap-1.5" data-testid="admin-tab-care"><LifeBuoy className="h-4 w-4" /> Cuidado</TabsTrigger>
          <TabsTrigger value="posts" className="rounded-full px-4 py-2 gap-1.5" data-testid="admin-tab-posts"><FileText className="h-4 w-4" /> Postagens</TabsTrigger>
          <TabsTrigger value="community" className="rounded-full px-4 py-2 gap-1.5" data-testid="admin-tab-community"><Radio className="h-4 w-4" /> Comunidade</TabsTrigger>
          <TabsTrigger value="users" className="rounded-full px-4 py-2 gap-1.5" data-testid="admin-tab-users"><UsersIcon className="h-4 w-4" /> Usuários</TabsTrigger>
          <TabsTrigger value="contacts" className="rounded-full px-4 py-2 gap-1.5" data-testid="admin-tab-contacts"><MessageSquare className="h-4 w-4" /> Contatos</TabsTrigger>
          <TabsTrigger value="reports" className="rounded-full px-4 py-2 gap-1.5" data-testid="admin-tab-reports"><FileDown className="h-4 w-4" /> Relatórios</TabsTrigger>
          <TabsTrigger value="rights" className="rounded-full px-4 py-2 gap-1.5" data-testid="admin-tab-rights"><BookOpenCheck className="h-4 w-4" /> Direitos</TabsTrigger>
          <TabsTrigger value="diagnostic" className="rounded-full px-4 py-2 gap-1.5" data-testid="admin-tab-diagnostic"><Stethoscope className="h-4 w-4" /> Diagnóstico</TabsTrigger>
          <TabsTrigger value="courses" className="rounded-full px-4 py-2 gap-1.5" data-testid="admin-tab-courses"><GraduationCap className="h-4 w-4" /> Educacional</TabsTrigger>
          <TabsTrigger value="whatsapp" className="rounded-full px-4 py-2 gap-1.5" data-testid="admin-tab-whatsapp"><MessageCircle className="h-4 w-4" /> WhatsApp</TabsTrigger>
          <TabsTrigger value="supporters" className="rounded-full px-4 py-2 gap-1.5" data-testid="admin-tab-supporters"><Users className="h-4 w-4" /> Apoiadores</TabsTrigger>
          <TabsTrigger value="subs" className="rounded-full px-4 py-2 gap-1.5" data-testid="admin-tab-subs"><Heart className="h-4 w-4" /> Doadores</TabsTrigger>
          <TabsTrigger value="shop" className="rounded-full px-4 py-2 gap-1.5" data-testid="admin-tab-shop"><ShoppingBag className="h-4 w-4" /> Loja</TabsTrigger>
          <TabsTrigger value="partners" className="rounded-full px-4 py-2 gap-1.5" data-testid="admin-tab-partners"><Handshake className="h-4 w-4" /> Parceiros</TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard"><DashboardTab /></TabsContent>
        <TabsContent value="care"><CareTab /></TabsContent>
        <TabsContent value="posts"><PostsTab /></TabsContent>
        <TabsContent value="community"><CommunityModerationTab /></TabsContent>
        <TabsContent value="users"><UsersTab /></TabsContent>
        <TabsContent value="contacts"><ContactsTab /></TabsContent>
        <TabsContent value="reports"><ReportsTab /></TabsContent>
        <TabsContent value="rights"><RightsTab /></TabsContent>
        <TabsContent value="diagnostic"><DiagnosticGuideTab /></TabsContent>
        <TabsContent value="courses"><CoursesTab /></TabsContent>
        <TabsContent value="whatsapp"><WhatsAppTab /></TabsContent>
        <TabsContent value="supporters"><SupportersTab /></TabsContent>
        <TabsContent value="subs"><SubscriptionsTab /></TabsContent>
        <TabsContent value="shop"><ShopTab /></TabsContent>
        <TabsContent value="partners"><PartnersTab /></TabsContent>
      </Tabs>
    </div>
  );
}

// ------------------------- Dashboard tab -------------------------
function DashboardTab() {
  const [stats, setStats] = useState({ users: 0, posts: 0, contacts: 0, by_region: {} });
  const [alerts, setAlerts] = useState([]);
  const [alertStats, setAlertStats] = useState({ open: 0, contacted: 0, resolved: 0 });
  useEffect(() => {
    api.get("/admin/stats").then(({ data }) => setStats(data));
    api.get("/admin/alerts", { params: { status: "open", limit: 5 } }).then(({ data }) => setAlerts(data)).catch(() => {});
    api.get("/admin/alerts/stats").then(({ data }) => setAlertStats(data)).catch(() => {});
  }, []);
  const cards = [
    { label: "Famílias cadastradas", value: stats.users, tone: "primary" },
    { label: "Postagens", value: stats.posts, tone: "secondary" },
    { label: "Contatos recebidos", value: stats.contacts, tone: "accent" },
  ];
  const openTotal = alertStats.open;
  return (
    <div className="space-y-8">
      <div className="grid gap-5 md:grid-cols-3">
        {cards.map((c) => (
          <div key={c.label} className="surface p-6" data-testid={`stat-card-${c.label.replace(/\s/g, '-')}`}>
            <div className="text-sm text-[hsl(var(--muted-foreground))]">{c.label}</div>
            <div className={`mt-2 font-heading text-4xl font-black text-[hsl(var(--${c.tone}))]`}>{c.value}</div>
          </div>
        ))}
      </div>

      <div className="surface p-6">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="font-heading text-lg font-bold flex items-center gap-2">
              <Heart className="h-4 w-4 text-[hsl(var(--secondary))]" /> Alertas de cuidado abertos
            </div>
            <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
              Famílias que podem precisar de atenção proativa. Abra a aba <span className="font-semibold">Cuidado</span> para acompanhar cada caso.
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Badge className="rounded-full bg-[hsl(var(--destructive))]/12 text-[hsl(var(--destructive))] border-transparent">Abertos: {alertStats.open}</Badge>
            <Badge className="rounded-full bg-[hsl(var(--accent))]/15 text-[hsl(var(--accent))] border-transparent">Em contato: {alertStats.contacted}</Badge>
            <Badge className="rounded-full bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))] border-transparent">Resolvidos: {alertStats.resolved}</Badge>
          </div>
        </div>
        <div className="mt-5 space-y-3">
          {openTotal === 0 && (
            <div className="rounded-2xl bg-[hsl(var(--muted))]/60 p-5 text-sm text-[hsl(var(--muted-foreground))]">
              Sem alertas abertos no momento. Quando uma postagem receber muitos abraços em pouco tempo, aparecerá aqui e no WhatsApp do instituto (quando o Z-API estiver ativo).
            </div>
          )}
          {alerts.map((a) => (
            <div key={a.id} className="rounded-2xl border border-[hsl(var(--secondary))]/30 bg-[hsl(var(--secondary))]/8 p-4" data-testid={`alert-${a.id}`}>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2 text-sm">
                  <Badge className="rounded-full bg-[hsl(var(--secondary))]/20 text-[hsl(var(--secondary))] border-transparent">
                    🤗 {a.hugs_count} abraços
                  </Badge>
                  <span className="font-heading font-bold">{a.anonymous ? "Família anônima" : a.author_name}</span>
                  <span className="text-xs text-[hsl(var(--muted-foreground))]">· {new Date(a.created_at).toLocaleString("pt-BR")}</span>
                </div>
                {a.whatsapp_sent ? (
                  <Badge className="rounded-full bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))] border-transparent">WhatsApp enviado</Badge>
                ) : (
                  <Badge variant="outline" className="rounded-full text-[hsl(var(--muted-foreground))]">
                    {a.whatsapp_error === "zapi-disabled" ? "Z-API não ativado" : "WhatsApp pendente"}
                  </Badge>
                )}
              </div>
              <div className="mt-2 text-sm text-[hsl(var(--foreground))]/90 italic">&ldquo;{a.excerpt}&rdquo;</div>
            </div>
          ))}
        </div>
      </div>

      <div className="surface p-6">
        <div className="font-heading text-lg font-bold">Famílias por região</div>
        <div className="mt-4 grid gap-3 sm:grid-cols-5">
          {BRAZIL_REGIONS.map((r) => (
            <div key={r} className="rounded-2xl bg-[hsl(var(--muted))] p-4">
              <div className="text-xs text-[hsl(var(--muted-foreground))]">{r}</div>
              <div className="mt-1 font-heading text-2xl font-black">{stats.by_region?.[r] || 0}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ------------------------- Posts tab -------------------------
const EMPTY_POST = { title: "", excerpt: "", content: "", cover_image: "", category: "Geral", tags: [], published: true };

function BackfillCoversButton({ onDone }) {
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("empty"); // "empty" | "all"
  const run = async () => {
    const regenerateAll = mode === "all";
    const label = regenerateAll ? "TODAS as capas (regerar tudo)" : "capas repetidas ou de banco de imagens";
    if (!window.confirm(`Vou gerar novas capas via IA para ${label} (postagens, cursos e comunidade). Roda em segundo plano e leva alguns minutos. Continuar?`)) return;
    setBusy(true);
    try {
      await api.post(`/admin/posts/backfill-covers?regenerate_all=${regenerateAll}&limit=100`);
      toast.success("Geração de capas iniciada. Atualize a página em alguns minutos para ver as novas imagens.");
      onDone && onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao gerar capas.");
    } finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-2">
      <select
        value={mode}
        onChange={(e) => setMode(e.target.value)}
        disabled={busy}
        className="h-9 rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-3 text-sm"
        data-testid="admin-backfill-mode"
      >
        <option value="empty">Só as repetidas (posts, cursos e comunidade)</option>
        <option value="all">Regerar todas</option>
      </select>
      <Button
        onClick={run}
        disabled={busy}
        variant="outline"
        className="btn-pill"
        data-testid="admin-backfill-covers-btn"
      >
        <ImageIcon className="mr-1.5 h-4 w-4" /> {busy ? "Gerando…" : "Gerar capas IA"}
      </Button>
    </div>
  );
}

function PostsTab() {
  const [posts, setPosts] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_POST);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const load = () => api.get("/admin/posts").then(({ data }) => setPosts(data));
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY_POST); setDialogOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      title: p.title || "", excerpt: p.excerpt || "", content: p.content || "",
      cover_image: p.cover_image || "", category: p.category || "Geral",
      tags: p.tags || [], published: p.published !== false,
    });
    setDialogOpen(true);
  };

  const onFileSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 6 * 1024 * 1024) {
      toast.error("Imagem muito grande (máx. 6MB).");
      e.target.value = "";
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/posts/upload-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((prev) => ({ ...prev, cover_image: data.url }));
      toast.success("Imagem enviada.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Não foi possível enviar a imagem.");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const save = async () => {
    if (!form.title || !form.content) {
      toast.error("Título e conteúdo são obrigatórios.");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/admin/posts/${editing.id}`, form);
        toast.success("Postagem atualizada.");
      } else {
        await api.post("/admin/posts", form);
        toast.success("Postagem publicada.");
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error("Não foi possível salvar.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/posts/${id}`);
      toast.success("Postagem excluída.");
      load();
    } catch (e) {
      toast.error("Não foi possível excluir.");
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-heading text-2xl font-extrabold">Postagens</h2>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">Publique artigos e novidades no timeline público.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <BackfillCoversButton onDone={load} />
          <Button onClick={openCreate} className="btn-pill bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90" data-testid="admin-new-post-btn">
            <Plus className="mr-1.5 h-4 w-4" /> Nova postagem
          </Button>
        </div>
      </div>

      <div className="surface overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Título</TableHead>
              <TableHead className="hidden md:table-cell">Categoria</TableHead>
              <TableHead className="hidden md:table-cell">Publicada</TableHead>
              <TableHead className="hidden lg:table-cell">Criada em</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {posts.map((p) => (
              <TableRow key={p.id} data-testid={`admin-post-row-${p.id}`}>
                <TableCell className="max-w-[360px]">
                  <div className="font-medium line-clamp-1">{p.title}</div>
                  <div className="text-xs text-[hsl(var(--muted-foreground))] line-clamp-1">{p.excerpt}</div>
                </TableCell>
                <TableCell className="hidden md:table-cell"><Badge variant="secondary" className="rounded-full">{p.category || "-"}</Badge></TableCell>
                <TableCell className="hidden md:table-cell">
                  {p.published ? <Badge className="rounded-full bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))] border-transparent">Sim</Badge> : <Badge variant="outline" className="rounded-full">Rascunho</Badge>}
                </TableCell>
                <TableCell className="hidden lg:table-cell text-sm text-[hsl(var(--muted-foreground))]">
                  {p.created_at ? new Date(p.created_at).toLocaleDateString("pt-BR") : ""}
                </TableCell>
                <TableCell className="text-right">
                  <div className="inline-flex gap-1.5">
                    <Button size="sm" variant="outline" className="rounded-full" onClick={() => openEdit(p)} data-testid={`admin-post-edit-${p.id}`}><Pencil className="h-4 w-4" /></Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="sm" variant="outline" className="rounded-full text-[hsl(var(--destructive))] border-[hsl(var(--destructive))]/40 hover:bg-[hsl(var(--destructive))]/10" data-testid={`admin-post-delete-${p.id}`}><Trash2 className="h-4 w-4" /></Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Excluir postagem?</AlertDialogTitle>
                          <AlertDialogDescription>Essa ação não pode ser desfeita.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancelar</AlertDialogCancel>
                          <AlertDialogAction onClick={() => remove(p.id)} data-testid={`admin-post-delete-confirm-${p.id}`}>Excluir</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {posts.length === 0 && (
              <TableRow><TableCell colSpan={5} className="text-center py-10 text-[hsl(var(--muted-foreground))]">Nenhuma postagem ainda.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Editar postagem" : "Nova postagem"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Título</Label>
              <Input className="mt-1.5 rounded-xl h-11" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="admin-post-title-input" />
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label>Categoria</Label>
                <Input className="mt-1.5 rounded-xl h-11" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="admin-post-category-input" />
              </div>
              <div>
                <Label>Imagem de capa</Label>
                <div className="mt-1.5 space-y-2">
                  <Input
                    className="rounded-xl h-11"
                    placeholder="Cole uma URL https://…"
                    value={form.cover_image}
                    onChange={(e) => setForm({ ...form, cover_image: e.target.value })}
                    data-testid="admin-post-image-input"
                  />
                  <div className="flex items-center gap-2">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      className="hidden"
                      onChange={onFileSelected}
                      data-testid="admin-post-image-file-input"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      className="rounded-full h-9 px-3 text-xs"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      data-testid="admin-post-image-upload-btn"
                    >
                      <Upload className="mr-1.5 h-3.5 w-3.5" />
                      {uploading ? "Enviando…" : "ou envie do computador"}
                    </Button>
                    {form.cover_image && (
                      <Button
                        type="button"
                        variant="ghost"
                        className="rounded-full h-9 px-3 text-xs text-[hsl(var(--muted-foreground))]"
                        onClick={() => setForm({ ...form, cover_image: "" })}
                        data-testid="admin-post-image-clear-btn"
                      >
                        Remover
                      </Button>
                    )}
                  </div>
                  {form.cover_image && (
                    <div className="rounded-xl overflow-hidden border border-[hsl(var(--border))] h-32 bg-[hsl(var(--muted))]" data-testid="admin-post-image-preview">
                      <img src={form.cover_image} alt="Prévia" className="w-full h-full object-cover" />
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div>
              <Label>Resumo</Label>
              <Textarea rows={2} className="mt-1.5 rounded-xl" value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} data-testid="admin-post-excerpt-input" />
            </div>
            <div>
              <Label>Conteúdo</Label>
              <Textarea rows={10} className="mt-1.5 rounded-xl" value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} data-testid="admin-post-content-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={save} disabled={saving} className="bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90" data-testid="admin-post-save-btn">
              {saving ? "Salvando…" : (editing ? "Salvar alterações" : "Publicar")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ------------------------- Users tab -------------------------
const EMPTY_USER = {
  email: "", password: "", name: "", phone: "",
  state: "", city: "", child_name: "", child_condition: "", child_birth_date: "",
  document_type: "CPF", document_number: "", role: "user",
};

function UsersTab() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [state, setState] = useState("__all__");
  const [region, setRegion] = useState("__all__");
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_USER);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    const params = {};
    if (state && state !== "__all__") params.state = state;
    if (region && region !== "__all__") params.region = region;
    if (search) params.search = search;
    api.get("/admin/users", { params })
      .then(({ data }) => setUsers(data))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [state, region]);

  const openCreate = () => { setEditing(null); setForm(EMPTY_USER); setDialogOpen(true); };
  const openEdit = (u) => {
    setEditing(u);
    setForm({
      email: u.email || "", password: "", name: u.name || "", phone: u.phone || "",
      state: u.state || "", city: u.city || "",
      child_name: u.child_name || "", child_condition: u.child_condition || "",
      child_birth_date: u.child_birth_date || "",
      document_type: u.document_type || "CPF", document_number: u.document_number || "",
      role: u.role || "user",
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Nome é obrigatório."); return; }
    if (!editing && (!form.email || !form.password)) { toast.error("E-mail e senha são obrigatórios para novo cadastro."); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      if (editing) {
        delete payload.email; delete payload.password;
        await api.put(`/admin/users/${editing.id}`, payload);
        toast.success("Cadastro atualizado.");
      } else {
        await api.post("/admin/users", payload);
        toast.success("Cadastro criado.");
      }
      setDialogOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao salvar."); } finally { setSaving(false); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Excluir cadastro de ${u.name}? Essa ação é irreversível.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("Cadastro excluído."); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao excluir."); }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl font-extrabold">Usuários cadastrados</h2>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">Filtre, edite ou inclua novos cadastros.</p>
        </div>
        <Button onClick={openCreate} className="btn-pill bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90" data-testid="admin-user-new-btn">
          <Plus className="h-4 w-4 mr-1.5" /> Novo cadastro
        </Button>
      </div>
      <div className="surface p-4 grid gap-3 sm:grid-cols-[1fr_180px_180px_120px]">
        <div className="relative">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[hsl(var(--muted-foreground))]" />
          <Input placeholder="Buscar por nome, e-mail ou cidade…" className="h-11 rounded-xl pl-10" value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} data-testid="admin-users-search" />
        </div>
        <Select value={region} onValueChange={setRegion}>
          <SelectTrigger className="h-11 rounded-xl" data-testid="admin-users-region-filter"><SelectValue placeholder="Região" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Todas as regiões</SelectItem>
            {BRAZIL_REGIONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={state} onValueChange={setState}>
          <SelectTrigger className="h-11 rounded-xl" data-testid="admin-users-state-filter"><SelectValue placeholder="Estado" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Todos os estados</SelectItem>
            {BRAZIL_STATES.map((s) => <SelectItem key={s.uf} value={s.uf}>{s.name} ({s.uf})</SelectItem>)}
          </SelectContent>
        </Select>
        <Button onClick={load} className="btn-pill h-11 bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90" data-testid="admin-users-apply-btn">Aplicar</Button>
      </div>

      <div className="surface overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nome</TableHead>
              <TableHead className="hidden md:table-cell">E-mail</TableHead>
              <TableHead className="hidden md:table-cell">Telefone</TableHead>
              <TableHead className="hidden lg:table-cell">Cidade / UF</TableHead>
              <TableHead>Papel</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={6} className="py-10 text-center text-[hsl(var(--muted-foreground))]">Carregando…</TableCell></TableRow>}
            {!loading && users.map((u) => (
              <TableRow key={u.id} data-testid={`admin-user-row-${u.id}`}>
                <TableCell>
                  <div className="font-medium">{u.name}</div>
                  {u.child_name && <div className="text-xs text-[hsl(var(--muted-foreground))]">criança: {u.child_name}</div>}
                </TableCell>
                <TableCell className="hidden md:table-cell text-sm">{u.email}</TableCell>
                <TableCell className="hidden md:table-cell text-sm">{u.phone || "-"}</TableCell>
                <TableCell className="hidden lg:table-cell text-sm">{u.city ? `${u.city} / ${u.state || "-"}` : (u.state || "-")}</TableCell>
                <TableCell>
                  {u.role === "admin"
                    ? <Badge className="rounded-full bg-[hsl(var(--secondary))]/20 text-[hsl(var(--secondary))] border-transparent">Admin</Badge>
                    : <Badge variant="outline" className="rounded-full">Família</Badge>}
                </TableCell>
                <TableCell className="text-right">
                  <div className="inline-flex gap-1.5">
                    <Button size="sm" variant="outline" className="rounded-full" onClick={() => openEdit(u)} data-testid={`admin-user-edit-${u.id}`}><Pencil className="h-4 w-4" /></Button>
                    <Button size="sm" variant="outline" className="rounded-full text-[hsl(var(--destructive))] border-[hsl(var(--destructive))]/40 hover:bg-[hsl(var(--destructive))]/10" onClick={() => remove(u)} data-testid={`admin-user-delete-${u.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {!loading && users.length === 0 && (
              <TableRow><TableCell colSpan={6} className="py-10 text-center text-[hsl(var(--muted-foreground))]">Nenhum usuário encontrado.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">{editing ? `Editar ${editing.name}` : "Novo cadastro"}</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-3">
            {!editing && (
              <>
                <div><Label>E-mail *</Label><Input className="mt-1.5 rounded-xl h-11" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="admin-user-email-input" /></div>
                <div><Label>Senha (mín. 6) *</Label><Input className="mt-1.5 rounded-xl h-11" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="admin-user-password-input" /></div>
              </>
            )}
            <div className="sm:col-span-2"><Label>Nome completo *</Label><Input className="mt-1.5 rounded-xl h-11" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="admin-user-name-input" /></div>
            <div><Label>WhatsApp</Label><Input className="mt-1.5 rounded-xl h-11" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="admin-user-phone-input" /></div>
            <div><Label>Papel</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="mt-1.5 h-11 rounded-xl" data-testid="admin-user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">Família</SelectItem>
                  <SelectItem value="admin">Administrador</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Estado</Label>
              <Select value={form.state} onValueChange={(v) => setForm({ ...form, state: v })}>
                <SelectTrigger className="mt-1.5 h-11 rounded-xl" data-testid="admin-user-state-select"><SelectValue placeholder="Selecionar" /></SelectTrigger>
                <SelectContent>
                  {BRAZIL_STATES.map((s) => <SelectItem key={s.uf} value={s.uf}>{s.name} ({s.uf})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Cidade</Label><Input className="mt-1.5 rounded-xl h-11" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="admin-user-city-input" /></div>
            <div><Label>Nome da criança</Label><Input className="mt-1.5 rounded-xl h-11" value={form.child_name} onChange={(e) => setForm({ ...form, child_name: e.target.value })} data-testid="admin-user-child-name-input" /></div>
            <div><Label>Data de nascimento</Label><Input className="mt-1.5 rounded-xl h-11" type="date" value={form.child_birth_date} onChange={(e) => setForm({ ...form, child_birth_date: e.target.value })} data-testid="admin-user-child-birth-input" /></div>
            <div className="sm:col-span-2"><Label>Situação neuroatípica</Label><Textarea rows={2} className="mt-1.5 rounded-xl" value={form.child_condition} onChange={(e) => setForm({ ...form, child_condition: e.target.value })} data-testid="admin-user-child-cond-input" /></div>
            <div><Label>Tipo de documento</Label>
              <Select value={form.document_type} onValueChange={(v) => setForm({ ...form, document_type: v })}>
                <SelectTrigger className="mt-1.5 h-11 rounded-xl" data-testid="admin-user-doctype-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="CPF">CPF</SelectItem>
                  <SelectItem value="RG">RG</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Número do documento</Label><Input className="mt-1.5 rounded-xl h-11" value={form.document_number} onChange={(e) => setForm({ ...form, document_number: e.target.value })} data-testid="admin-user-docnum-input" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={save} disabled={saving} className="bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90" data-testid="admin-user-save-btn">
              {saving ? "Salvando…" : (editing ? "Salvar alterações" : "Criar cadastro")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ------------------------- Reports & Donors tab -------------------------
const MONTHS_PT = ["Janeiro","Fevereiro","Março","Abril","Maio","Junho","Julho","Agosto","Setembro","Outubro","Novembro","Dezembro"];

function ReportsTab() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [dispatches, setDispatches] = useState([]);
  const [donorsCount, setDonorsCount] = useState({ active: 0, total: 0 });
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/admin/reports/monthly", { params: { year, month } })
      .then(({ data }) => setReport(data))
      .finally(() => setLoading(false));
    api.get("/admin/reports/dispatches", { params: { limit: 10 } }).then(({ data }) => setDispatches(data));
    api.get("/admin/donors").then(({ data }) => {
      setDonorsCount({ total: data.length, active: data.filter((d) => d.active).length });
    });
  };
  useEffect(() => { load(); }, [year, month]);

  const fetchPdfBlob = async () => {
    const { data } = await api.get("/admin/reports/monthly.pdf", { params: { year, month }, responseType: "blob" });
    return URL.createObjectURL(data);
  };

  const previewPdf = async () => {
    try {
      const url = await fetchPdfBlob();
      setPreviewUrl(url); setPreviewOpen(true);
    } catch (_) { toast.error("Não foi possível carregar a prévia."); }
  };

  const downloadPdf = async () => {
    try {
      const url = await fetchPdfBlob();
      const a = document.createElement("a");
      a.href = url; a.download = `instituto-cuidar-${year}-${String(month).padStart(2, "0")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 3000);
    } catch (_) { toast.error("Não foi possível baixar o PDF."); }
  };

  const sendToDonors = async () => {
    if (!window.confirm(`Enviar o relatório de ${MONTHS_PT[month - 1]}/${year} para ${donorsCount.active} doador(es) ativo(s) via WhatsApp?`)) return;
    setSending(true);
    try {
      const { data } = await api.post("/admin/reports/send", { year, month, only_active: true });
      if (data.zapi_enabled) {
        toast.success(`Enviado para ${data.sent_count}/${data.recipients_total} doadores.`);
      } else {
        toast.warning(`Z-API ainda não ativado. ${data.recipients_total} doador(es) na fila — o envio será registrado assim que o Z-API for ligado.`);
      }
      const d = await api.get("/admin/reports/dispatches", { params: { limit: 10 } });
      setDispatches(d.data);
    } catch (_) { toast.error("Erro ao enviar."); } finally { setSending(false); }
  };

  const years = Array.from({ length: 6 }, (_, i) => now.getFullYear() - 3 + i);

  return (
    <div className="space-y-8">
      <div>
        <h2 className="font-heading text-2xl font-extrabold flex items-center gap-2">
          <FileDown className="h-6 w-6 text-[hsl(var(--primary))]" /> Relatórios mensais
        </h2>
        <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
          Prestação de contas para doadores, editais e parceiros. Gere o PDF do mês e envie no WhatsApp de cada apoiador ativo.
        </p>
      </div>

      <LogoConfig />
      <ShopToggle />

      <div className="surface p-6 sm:p-7">
        <div className="grid gap-4 sm:grid-cols-[1fr_1fr_auto_auto_auto] items-end">
          <div>
            <Label>Mês</Label>
            <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
              <SelectTrigger className="h-11 rounded-xl mt-1.5" data-testid="report-month-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {MONTHS_PT.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Ano</Label>
            <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
              <SelectTrigger className="h-11 rounded-xl mt-1.5" data-testid="report-year-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button variant="outline" onClick={previewPdf} className="btn-pill h-11" data-testid="report-preview-btn">
            <Eye className="h-4 w-4 mr-1.5" /> Ver prévia
          </Button>
          <Button variant="outline" onClick={downloadPdf} className="btn-pill h-11" data-testid="report-download-btn">
            <Download className="h-4 w-4 mr-1.5" /> Baixar PDF
          </Button>
          <Button
            onClick={sendToDonors}
            disabled={sending || donorsCount.active === 0}
            className="btn-pill h-11 bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90"
            data-testid="report-send-btn"
          >
            <Send className="h-4 w-4 mr-1.5" /> {sending ? "Enviando…" : `Enviar aos doadores (${donorsCount.active})`}
          </Button>
        </div>
        {donorsCount.active === 0 && (
          <div className="mt-4 rounded-2xl bg-[hsl(var(--muted))]/70 px-4 py-3 text-sm text-[hsl(var(--muted-foreground))]">
            Nenhum doador ativo ainda. Cadastre doadores na seção abaixo para começar os envios.
          </div>
        )}
      </div>

      <ScheduleConfig />

      {loading && <div className="text-sm text-[hsl(var(--muted-foreground))]">Calculando…</div>}
      {!loading && report && (
        <div className="surface p-6">
          <div className="text-xs font-semibold uppercase tracking-widest text-[hsl(var(--secondary))]">Prévia dos números — {MONTHS_PT[report.period.month - 1]}/{report.period.year}</div>
          <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatMini label="Novas famílias" value={report.new_users} tone="primary" />
            <StatMini label="Postagens institucionais" value={report.new_posts} tone="secondary" />
            <StatMini label="Postagens da comunidade" value={report.new_feed} tone="accent" />
            <StatMini label="Contatos recebidos" value={report.new_contacts} tone="primary" />
            <StatMini label="Alertas de cuidado" value={report.new_alerts} tone="destructive" />
            <StatMini label="Alertas resolvidos" value={report.resolved_alerts} tone="primary" />
            <StatMini label="Curtidas" value={report.engagement?.likes || 0} tone="destructive" />
            <StatMini label="Abraços 🤗" value={report.engagement?.hugs || 0} tone="secondary" />
          </div>
        </div>
      )}

      <DispatchesList dispatches={dispatches} />

      <DonorsSection onChange={load} />

      <Dialog open={previewOpen} onOpenChange={(v) => {
        setPreviewOpen(v);
        if (!v && previewUrl) { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }
      }}>
        <DialogContent className="max-w-4xl h-[85vh] flex flex-col p-0 overflow-hidden">
          <DialogHeader className="p-5 border-b border-[hsl(var(--border))]">
            <DialogTitle className="font-heading">Prévia — {MONTHS_PT[month - 1]}/{year}</DialogTitle>
          </DialogHeader>
          {previewUrl && (
            <iframe title="Prévia do relatório" src={previewUrl} className="flex-1 w-full" data-testid="report-preview-iframe" />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ShopToggle() {
  const { shopEnabled, refresh } = useAppConfig();
  const [saving, setSaving] = useState(false);

  const toggle = async () => {
    setSaving(true);
    try {
      await api.post("/admin/config/shop", { enabled: !shopEnabled });
      await refresh();
      toast.success(shopEnabled ? "Loja ocultada do site." : "Loja ativada no site.");
    } catch (_) {
      toast.error("Não foi possível salvar.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="surface p-6" data-testid="admin-shop-toggle-card">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="font-heading text-lg font-bold flex items-center gap-2">
            <ShoppingBag className="h-5 w-5 text-[hsl(var(--primary))]" /> Loja Solidária no site
          </div>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
            Enquanto <b>ocultada</b>, o menu "Produtos" e o carrinho somem, e as
            rotas <code>/produtos</code> e <code>/carrinho</code> mostram uma tela
            de "em breve". Use ocultada durante a análise da Apple Store — a Apple
            exige que doações e vendas físicas sejam externas ao app.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className={`text-sm font-bold ${shopEnabled ? "text-[hsl(var(--primary))]" : "text-[hsl(var(--muted-foreground))]"}`}>
            {shopEnabled ? "Ativada" : "Ocultada"}
          </span>
          <Button
            onClick={toggle}
            disabled={saving}
            variant="outline"
            className={`rounded-full h-11 ${shopEnabled ? "border-[hsl(var(--primary))]/40" : ""}`}
            data-testid="admin-shop-toggle-btn"
          >
            {shopEnabled ? <ToggleRight className="h-5 w-5 mr-1.5 text-[hsl(var(--primary))]" /> : <ToggleLeft className="h-5 w-5 mr-1.5" />}
            {saving ? "Salvando…" : (shopEnabled ? "Ocultar loja" : "Ativar loja")}
          </Button>
        </div>
      </div>
    </div>
  );
}

function LogoConfig() {
  const [logo, setLogo] = useState({ path: null });
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const load = () => api.get("/admin/config/logo").then(({ data }) => setLogo(data));
  useEffect(() => { load(); }, []);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const form = new FormData(); form.append("file", f);
      await api.post("/admin/config/logo", form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Logo atualizado.");
      load();
    } catch (_) { toast.error("Não foi possível enviar o logo."); }
    finally { setUploading(false); if (inputRef.current) inputRef.current.value = ""; }
  };

  const remove = async () => {
    if (!window.confirm("Remover o logo do relatório?")) return;
    await api.delete("/admin/config/logo");
    toast.success("Logo removido.");
    load();
  };

  return (
    <div className="surface p-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="font-heading text-lg font-bold flex items-center gap-2">
            <ImageIcon className="h-5 w-5 text-[hsl(var(--primary))]" /> Logo do instituto no relatório
          </div>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
            PNG ou JPG transparente/quadrado é o ideal. Aparece no cabeçalho de todas as páginas do PDF.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {logo.path ? (
            <AuthImage path={logo.path} className="h-14 w-14 object-contain bg-[hsl(var(--muted))] rounded-2xl p-1.5" alt="Logo atual" />
          ) : (
            <div className="h-14 w-14 grid place-items-center bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))] text-xs rounded-2xl">sem logo</div>
          )}
          <input ref={inputRef} type="file" accept="image/*" hidden onChange={onFile} data-testid="logo-file-input" />
          <Button variant="outline" onClick={() => inputRef.current?.click()} disabled={uploading} className="rounded-full" data-testid="logo-upload-btn">
            <Upload className="h-4 w-4 mr-1.5" /> {uploading ? "Enviando…" : (logo.path ? "Trocar" : "Enviar logo")}
          </Button>
          {logo.path && (
            <Button variant="outline" onClick={remove} className="rounded-full text-[hsl(var(--destructive))] border-[hsl(var(--destructive))]/40 hover:bg-[hsl(var(--destructive))]/10" data-testid="logo-remove-btn">
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function ScheduleConfig() {
  const [s, setS] = useState({ enabled: false, day: 5, hour: 9, minute: 0, next_run: null });
  const [saving, setSaving] = useState(false);
  const [runningNow, setRunningNow] = useState(false);

  const load = () => api.get("/admin/reports/schedule").then(({ data }) => setS(data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/reports/schedule", {
        enabled: s.enabled, day: Number(s.day), hour: Number(s.hour), minute: Number(s.minute || 0),
      });
      setS(data);
      toast.success(s.enabled ? "Envio automático ativado." : "Envio automático desativado.");
    } catch (_) { toast.error("Erro ao salvar agendamento."); } finally { setSaving(false); }
  };

  const runNow = async () => {
    if (!window.confirm("Executar o envio agora (usará o mês anterior como referência)?")) return;
    setRunningNow(true);
    try {
      await api.post("/admin/reports/schedule/run-now");
      toast.success("Job executado. Veja em 'Envios recentes'.");
    } catch (_) { toast.error("Erro ao executar."); } finally { setRunningNow(false); }
  };

  return (
    <div className="surface p-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="font-heading text-lg font-bold flex items-center gap-2">
            <CalendarClock className="h-5 w-5 text-[hsl(var(--accent))]" /> Envio automático mensal
          </div>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
            No dia e horário definidos, o sistema gera o relatório do <strong>mês anterior</strong> e envia via WhatsApp para todos os doadores ativos.
          </p>
        </div>
        <label className="inline-flex items-center gap-3 cursor-pointer">
          <span className="text-sm">{s.enabled ? "Ativo" : "Desligado"}</span>
          <button
            type="button"
            onClick={() => setS({ ...s, enabled: !s.enabled })}
            data-testid="schedule-toggle"
            aria-label="Alternar envio automático"
          >
            {s.enabled
              ? <ToggleRight className="h-8 w-8 text-[hsl(var(--primary))]" />
              : <ToggleLeft className="h-8 w-8 text-[hsl(var(--muted-foreground))]" />}
          </button>
        </label>
      </div>
      <div className="mt-4 grid gap-3 sm:grid-cols-[140px_120px_120px_auto_auto] items-end">
        <div>
          <Label>Dia do mês</Label>
          <Input type="number" min="1" max="28" className="h-11 rounded-xl mt-1.5" value={s.day} onChange={(e) => setS({ ...s, day: e.target.value })} data-testid="schedule-day-input" />
        </div>
        <div>
          <Label>Hora</Label>
          <Input type="number" min="0" max="23" className="h-11 rounded-xl mt-1.5" value={s.hour} onChange={(e) => setS({ ...s, hour: e.target.value })} data-testid="schedule-hour-input" />
        </div>
        <div>
          <Label>Minuto</Label>
          <Input type="number" min="0" max="59" className="h-11 rounded-xl mt-1.5" value={s.minute || 0} onChange={(e) => setS({ ...s, minute: e.target.value })} data-testid="schedule-minute-input" />
        </div>
        <Button onClick={save} disabled={saving} className="btn-pill h-11 bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90" data-testid="schedule-save-btn">
          {saving ? "Salvando…" : "Salvar"}
        </Button>
        <Button variant="outline" onClick={runNow} disabled={runningNow} className="btn-pill h-11" data-testid="schedule-runnow-btn">
          {runningNow ? "Executando…" : "Testar agora"}
        </Button>
      </div>
      {s.next_run && (
        <div className="mt-3 text-xs text-[hsl(var(--muted-foreground))]">
          Próxima execução: <strong>{new Date(s.next_run).toLocaleString("pt-BR")}</strong> (fuso {s.timezone || "America/Sao_Paulo"})
        </div>
      )}
    </div>
  );
}

function StatMini({ label, value, tone }) {
  return (
    <div className="rounded-2xl bg-[hsl(var(--muted))] p-4">
      <div className="text-xs text-[hsl(var(--muted-foreground))]">{label}</div>
      <div className={`mt-1 font-heading text-2xl font-black text-[hsl(var(--${tone}))]`}>{value}</div>
    </div>
  );
}

function DispatchesList({ dispatches }) {
  if (!dispatches?.length) return null;
  return (
    <div className="surface p-6">
      <div className="font-heading text-lg font-bold">Envios recentes</div>
      <div className="mt-4 space-y-2">
        {dispatches.map((d) => (
          <div key={d.id} className="rounded-2xl bg-[hsl(var(--muted))]/60 px-4 py-3 flex flex-wrap items-center justify-between gap-3 text-sm" data-testid={`dispatch-${d.id}`}>
            <div>
              <div className="font-heading font-bold">{MONTHS_PT[(d.period?.month || 1) - 1]}/{d.period?.year}</div>
              <div className="text-xs text-[hsl(var(--muted-foreground))]">por {d.sent_by_name} · {new Date(d.created_at).toLocaleString("pt-BR")}</div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Badge className="rounded-full bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))] border-transparent">Enviados: {d.sent_count}</Badge>
              {d.failed_count > 0 && <Badge className="rounded-full bg-[hsl(var(--destructive))]/12 text-[hsl(var(--destructive))] border-transparent">Falhas: {d.failed_count}</Badge>}
              <Badge variant="outline" className="rounded-full">Total: {d.recipients_total}</Badge>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const EMPTY_DONOR = { name: "", phone: "", email: "", monthly_amount: 0, active: true, notes: "" };

function DonorsSection({ onChange }) {
  const [donors, setDonors] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_DONOR);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/donors").then(({ data }) => setDonors(data));
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY_DONOR); setDialogOpen(true); };
  const openEdit = (d) => {
    setEditing(d);
    setForm({
      name: d.name || "", phone: d.phone || "", email: d.email || "",
      monthly_amount: d.monthly_amount || 0, active: d.active !== false, notes: d.notes || "",
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.name || !form.phone) { toast.error("Nome e WhatsApp são obrigatórios."); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/admin/donors/${editing.id}`, form);
        toast.success("Doador atualizado.");
      } else {
        await api.post("/admin/donors", form);
        toast.success("Doador cadastrado.");
      }
      setDialogOpen(false); load(); onChange && onChange();
    } catch (_) { toast.error("Erro ao salvar."); } finally { setSaving(false); }
  };

  const toggleActive = async (d) => {
    try {
      await api.put(`/admin/donors/${d.id}`, { ...d, active: !d.active });
      load(); onChange && onChange();
    } catch (_) { toast.error("Erro ao atualizar status."); }
  };

  const remove = async (d) => {
    if (!window.confirm(`Excluir ${d.name} da lista de doadores?`)) return;
    try {
      await api.delete(`/admin/donors/${d.id}`);
      load(); onChange && onChange();
      toast.success("Doador excluído.");
    } catch (_) { toast.error("Erro ao excluir."); }
  };

  return (
    <div className="surface p-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="font-heading text-lg font-bold flex items-center gap-2">
            <HeartHandshake className="h-5 w-5 text-[hsl(var(--secondary))]" /> Doadores mensais
          </div>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
            Cadastre os apoiadores que doam todo mês. O relatório é enviado no WhatsApp de cada um marcado como ativo.
          </p>
        </div>
        <Button onClick={openCreate} className="btn-pill bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90" data-testid="donor-new-btn">
          <Plus className="h-4 w-4 mr-1.5" /> Novo doador
        </Button>
      </div>

      <div className="mt-5 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nome</TableHead>
              <TableHead>WhatsApp</TableHead>
              <TableHead className="hidden md:table-cell">E-mail</TableHead>
              <TableHead className="text-right">Valor/mês</TableHead>
              <TableHead>Ativo</TableHead>
              <TableHead className="hidden lg:table-cell">Último envio</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {donors.map((d) => (
              <TableRow key={d.id} data-testid={`donor-row-${d.id}`}>
                <TableCell className="font-medium">{d.name}</TableCell>
                <TableCell className="text-sm">{d.phone}</TableCell>
                <TableCell className="hidden md:table-cell text-sm">{d.email || "-"}</TableCell>
                <TableCell className="text-right text-sm">R$ {Number(d.monthly_amount || 0).toFixed(2)}</TableCell>
                <TableCell>
                  <button onClick={() => toggleActive(d)} data-testid={`donor-toggle-${d.id}`} aria-label="Alternar ativo">
                    {d.active
                      ? <ToggleRight className="h-6 w-6 text-[hsl(var(--primary))]" />
                      : <ToggleLeft className="h-6 w-6 text-[hsl(var(--muted-foreground))]" />}
                  </button>
                </TableCell>
                <TableCell className="hidden lg:table-cell text-xs text-[hsl(var(--muted-foreground))]">
                  {d.last_sent_at ? new Date(d.last_sent_at).toLocaleDateString("pt-BR") : "—"}
                </TableCell>
                <TableCell className="text-right">
                  <div className="inline-flex gap-1.5">
                    <Button size="sm" variant="outline" className="rounded-full" onClick={() => openEdit(d)} data-testid={`donor-edit-${d.id}`}><Pencil className="h-4 w-4" /></Button>
                    <Button size="sm" variant="outline" className="rounded-full text-[hsl(var(--destructive))] border-[hsl(var(--destructive))]/40 hover:bg-[hsl(var(--destructive))]/10" onClick={() => remove(d)} data-testid={`donor-delete-${d.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {donors.length === 0 && (
              <TableRow><TableCell colSpan={7} className="py-10 text-center text-[hsl(var(--muted-foreground))]">Nenhum doador cadastrado.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Editar doador" : "Novo doador"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nome</Label>
              <Input className="mt-1.5 rounded-xl h-11" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="donor-name-input" />
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label>WhatsApp (com DDI+DDD)</Label>
                <Input className="mt-1.5 rounded-xl h-11" placeholder="5511999999999" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="donor-phone-input" />
              </div>
              <div>
                <Label>E-mail (opcional)</Label>
                <Input className="mt-1.5 rounded-xl h-11" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="donor-email-input" />
              </div>
              <div>
                <Label>Valor mensal (R$)</Label>
                <Input className="mt-1.5 rounded-xl h-11" type="number" step="1" min="0" value={form.monthly_amount} onChange={(e) => setForm({ ...form, monthly_amount: Number(e.target.value) })} data-testid="donor-amount-input" />
              </div>
              <div className="flex items-end">
                <label className="inline-flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="donor-active-checkbox" className="h-4 w-4 rounded" />
                  Ativo (receberá envios)
                </label>
              </div>
            </div>
            <div>
              <Label>Notas (opcional)</Label>
              <Textarea rows={2} className="mt-1.5 rounded-xl" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="donor-notes-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={save} disabled={saving} className="bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90" data-testid="donor-save-btn">
              {saving ? "Salvando…" : (editing ? "Salvar" : "Cadastrar")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ------------------------- Care (follow-up trail) tab -------------------------
const STATUS_META = {
  open: { label: "Aberto", color: "bg-[hsl(var(--destructive))]/12 text-[hsl(var(--destructive))]", icon: Clock },
  contacted: { label: "Em contato", color: "bg-[hsl(var(--accent))]/15 text-[hsl(var(--accent))]", icon: Phone },
  resolved: { label: "Resolvido", color: "bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))]", icon: CheckCircle2 },
};

function CareTab() {
  const [alerts, setAlerts] = useState([]);
  const [filter, setFilter] = useState("open");
  const [stats, setStats] = useState({ open: 0, contacted: 0, resolved: 0 });
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    const params = filter === "all" ? {} : { status: filter };
    Promise.all([
      api.get("/admin/alerts", { params }).then(({ data }) => setAlerts(data)),
      api.get("/admin/alerts/stats").then(({ data }) => setStats(data)),
    ]).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [filter]);

  const updateStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/admin/alerts/${id}`, { status });
      setAlerts((prev) => prev.map((a) => (a.id === id ? data : a)));
      const s = await api.get("/admin/alerts/stats"); setStats(s.data);
      toast.success("Status atualizado.");
    } catch (_) { toast.error("Falha ao atualizar."); }
  };

  const addNote = async (id, text, clearInput) => {
    if (!text.trim()) return;
    try {
      const { data } = await api.post(`/admin/alerts/${id}/notes`, { text });
      setAlerts((prev) => prev.map((a) => (a.id === id ? { ...a, notes: [...a.notes, data] } : a)));
      clearInput();
    } catch (_) { toast.error("Falha ao adicionar nota."); }
  };

  const filters = [
    { key: "open", label: "Abertos", count: stats.open },
    { key: "contacted", label: "Em contato", count: stats.contacted },
    { key: "resolved", label: "Resolvidos", count: stats.resolved },
    { key: "all", label: "Todos", count: stats.open + stats.contacted + stats.resolved },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-heading text-2xl font-extrabold flex items-center gap-2">
          <LifeBuoy className="h-6 w-6 text-[hsl(var(--secondary))]" /> Trilha de cuidado
        </h2>
        <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
          Cada alerta de abraço vira uma tarefa: entre em contato com a família, registre notas e marque como resolvido.
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        {filters.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            data-testid={`care-filter-${f.key}`}
            className={`btn-pill rounded-full px-4 py-2 text-sm inline-flex items-center gap-2 border transition-colors ${
              filter === f.key
                ? "bg-[hsl(var(--foreground))] text-white border-transparent"
                : "bg-white text-[hsl(var(--foreground))] border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))]"
            }`}
          >
            {f.label}
            <span className={`text-xs rounded-full px-2 py-0.5 ${filter === f.key ? "bg-white/20" : "bg-[hsl(var(--muted))]"}`}>{f.count}</span>
          </button>
        ))}
      </div>

      {loading && <div className="text-[hsl(var(--muted-foreground))] text-sm">Carregando…</div>}
      {!loading && alerts.length === 0 && (
        <div className="surface p-10 text-center text-[hsl(var(--muted-foreground))]">
          Nenhum alerta {filter === "all" ? "" : `${filters.find((x) => x.key === filter)?.label.toLowerCase() || ""}`} por aqui.
        </div>
      )}

      <div className="space-y-4">
        {alerts.map((a) => <CareCard key={a.id} alert={a} onUpdateStatus={updateStatus} onAddNote={addNote} />)}
      </div>
    </div>
  );
}

function CareCard({ alert, onUpdateStatus, onAddNote }) {
  const [note, setNote] = useState("");
  const status = STATUS_META[alert.status] || STATUS_META.open;
  const StatusIcon = status.icon;
  return (
    <div className="surface p-5 sm:p-6" data-testid={`care-card-${alert.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge className={`rounded-full border-transparent ${status.color}`}>
              <StatusIcon className="h-3 w-3 mr-1" /> {status.label}
            </Badge>
            <Badge className="rounded-full bg-[hsl(var(--secondary))]/20 text-[hsl(var(--secondary))] border-transparent">
              🤗 {alert.hugs_count} abraços
            </Badge>
            <span className="font-heading font-bold">{alert.anonymous ? "Família anônima" : alert.author_name}</span>
            <span className="text-xs text-[hsl(var(--muted-foreground))]">· {new Date(alert.created_at).toLocaleString("pt-BR")}</span>
          </div>
          <div className="mt-2 text-sm text-[hsl(var(--foreground))]/90 italic">&ldquo;{alert.excerpt}&rdquo;</div>
          <div className="mt-2 text-xs text-[hsl(var(--muted-foreground))]">
            WhatsApp: {alert.whatsapp_sent
              ? <span className="text-[hsl(var(--primary))] font-medium">enviado ao instituto</span>
              : (alert.whatsapp_error === "zapi-disabled" ? "Z-API não ativado" : "pendente")}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {alert.status !== "contacted" && alert.status !== "resolved" && (
            <Button size="sm" variant="outline" className="rounded-full" onClick={() => onUpdateStatus(alert.id, "contacted")} data-testid={`care-start-${alert.id}`}>
              <Phone className="h-4 w-4 mr-1.5" /> Iniciar contato
            </Button>
          )}
          {alert.status !== "resolved" && (
            <Button size="sm" className="rounded-full bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90" onClick={() => onUpdateStatus(alert.id, "resolved")} data-testid={`care-resolve-${alert.id}`}>
              <CheckCircle2 className="h-4 w-4 mr-1.5" /> Marcar resolvido
            </Button>
          )}
          {alert.status === "resolved" && (
            <Button size="sm" variant="outline" className="rounded-full" onClick={() => onUpdateStatus(alert.id, "open")} data-testid={`care-reopen-${alert.id}`}>
              Reabrir
            </Button>
          )}
        </div>
      </div>

      <div className="mt-5 border-t border-[hsl(var(--border))] pt-4">
        <div className="text-xs font-semibold uppercase tracking-widest text-[hsl(var(--muted-foreground))] mb-3">
          Anotações da equipe ({alert.notes.length})
        </div>
        <div className="space-y-2">
          {alert.notes.map((n) => (
            <div key={n.id} className="rounded-2xl bg-[hsl(var(--muted))] px-4 py-2.5" data-testid={`care-note-${n.id}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-heading font-bold">{n.author_name}</span>
                <span className="text-[11px] text-[hsl(var(--muted-foreground))]">{new Date(n.created_at).toLocaleString("pt-BR")}</span>
              </div>
              <div className="text-sm mt-1 whitespace-pre-wrap">{n.text}</div>
            </div>
          ))}
        </div>
        <div className="mt-3 flex gap-2">
          <Input
            placeholder="Registrar contato, retorno da família, próximos passos…"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { onAddNote(alert.id, note, () => setNote("")); } }}
            className="rounded-full h-10 bg-white"
            data-testid={`care-note-input-${alert.id}`}
          />
          <Button
            size="icon"
            onClick={() => onAddNote(alert.id, note, () => setNote(""))}
            disabled={!note.trim()}
            className="rounded-full bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90"
            data-testid={`care-note-send-${alert.id}`}
            aria-label="Adicionar nota"
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

// ------------------------- Community moderation tab -------------------------
function SeedControls({ onDone }) {
  const [loading, setLoading] = useState(false);

  const unseed = async () => {
    if (!window.confirm("Remover TODOS os posts, comentários e usuários fake da comunidade? Essa ação não pode ser desfeita — use apenas quando o engajamento orgânico estiver saudável.")) return;
    setLoading(true);
    try {
      const { data } = await api.post("/admin/community/unseed");
      toast.success(`${data.posts_removed} posts fake removidos + ${data.users_removed} usuários fake.`);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao remover seed.");
    } finally {
      setLoading(false);
    }
  };

  const reseed = async () => {
    if (!window.confirm("Recriar o seed (remove tudo e cria de novo)?")) return;
    setLoading(true);
    try {
      await api.post("/admin/community/reseed");
      toast.success("Seed recriado com sucesso.");
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao recriar seed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-end gap-2 shrink-0">
      <div className="inline-flex items-center gap-2 rounded-full bg-amber-50 border border-amber-200 px-3 py-1.5 text-[11px] font-semibold text-amber-800">
        🌱 Cold-start ativo
      </div>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={reseed}
          disabled={loading}
          data-testid="admin-community-reseed-btn"
          className="text-xs px-3 py-1.5 rounded-full border border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))] disabled:opacity-40"
        >
          Recriar seed
        </button>
        <button
          type="button"
          onClick={unseed}
          disabled={loading}
          data-testid="admin-community-unseed-btn"
          className="text-xs px-3 py-1.5 rounded-full bg-red-50 border border-red-200 text-red-700 font-semibold hover:bg-red-100 disabled:opacity-40"
        >
          {loading ? "Removendo…" : "Remover fakes"}
        </button>
      </div>
    </div>
  );
}


function CommunityModerationTab() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () =>
    api.get("/feed", { params: { limit: 60 } })
      .then(({ data }) => setPosts(data))
      .finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const removePost = async (id) => {
    if (!window.confirm("Excluir esta postagem da comunidade?")) return;
    try {
      await api.delete(`/feed/${id}`);
      setPosts((prev) => prev.filter((p) => p.id !== id));
      toast.success("Postagem excluída.");
    } catch (_) { toast.error("Falha ao excluir."); }
  };

  const removeComment = async (postId, commentId) => {
    try {
      await api.delete(`/feed/${postId}/comments/${commentId}`);
      setPosts((prev) => prev.map((p) => p.id === postId
        ? { ...p, comments: p.comments.filter((c) => c.id !== commentId), comments_count: p.comments_count - 1 }
        : p));
      toast.success("Comentário excluído.");
    } catch (_) { toast.error("Falha ao excluir."); }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl font-extrabold">Moderação da comunidade</h2>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">Todas as postagens e comentários feitos por famílias e equipe.</p>
        </div>
        <SeedControls onDone={load} />
      </div>
      {loading && <div className="text-[hsl(var(--muted-foreground))]">Carregando…</div>}
      {!loading && posts.length === 0 && (
        <div className="surface p-10 text-center text-[hsl(var(--muted-foreground))]">Nenhuma postagem ainda.</div>
      )}
      <div className="space-y-4">
        {posts.map((p) => (
          <div key={p.id} className="surface p-5" data-testid={`mod-post-${p.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2 text-sm">
                  <span className="font-heading font-bold">{p.author_name}</span>
                  {p.author_role === "admin" && <Badge className="rounded-full bg-[hsl(var(--secondary))]/20 text-[hsl(var(--secondary))] border-transparent">Equipe</Badge>}
                  <span className="text-xs text-[hsl(var(--muted-foreground))]">· {new Date(p.created_at).toLocaleString("pt-BR")}</span>
                </div>
                <div className="mt-1 text-xs text-[hsl(var(--muted-foreground))] inline-flex items-center gap-3">
                  <span className="inline-flex items-center gap-1"><Heart className="h-3.5 w-3.5" /> {p.likes_count}</span>
                  <span className="inline-flex items-center gap-1"><MessageSquare className="h-3.5 w-3.5" /> {p.comments_count}</span>
                </div>
              </div>
              <Button size="sm" variant="outline" onClick={() => removePost(p.id)} className="rounded-full text-[hsl(var(--destructive))] border-[hsl(var(--destructive))]/40 hover:bg-[hsl(var(--destructive))]/10" data-testid={`mod-post-delete-${p.id}`}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
            <div className="mt-3 text-sm whitespace-pre-wrap">{p.text}</div>
            {p.comments?.length > 0 && (
              <div className="mt-4 border-t border-[hsl(var(--border))] pt-3 space-y-2">
                {p.comments.map((c) => (
                  <div key={c.id} className="flex items-start justify-between gap-3 text-sm" data-testid={`mod-comment-${c.id}`}>
                    <div>
                      <span className="font-heading font-bold text-xs">{c.author_name}</span>
                      <span className="text-[11px] text-[hsl(var(--muted-foreground))] ml-2">{new Date(c.created_at).toLocaleString("pt-BR")}</span>
                      <div className="mt-0.5">{c.text}</div>
                    </div>
                    <button onClick={() => removeComment(p.id, c.id)} className="p-1.5 rounded-full hover:bg-[hsl(var(--destructive))]/10 text-[hsl(var(--destructive))]" data-testid={`mod-comment-delete-${c.id}`} aria-label="Excluir">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ------------------------- Contacts tab -------------------------
function ContactsTab() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/admin/contacts").then(({ data }) => setContacts(data)).finally(() => setLoading(false));
  }, []);
  return (
    <div className="space-y-5">
      <div>
        <h2 className="font-heading text-2xl font-extrabold">Contatos recebidos</h2>
        <p className="text-sm text-[hsl(var(--muted-foreground))]">Mensagens do formulário público. Integração Z-API disponível quando ativada.</p>
      </div>
      <div className="surface overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Data</TableHead>
              <TableHead>Nome</TableHead>
              <TableHead className="hidden md:table-cell">Telefone</TableHead>
              <TableHead>Mensagem</TableHead>
              <TableHead className="text-right">WhatsApp</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={5} className="py-10 text-center text-[hsl(var(--muted-foreground))]">Carregando…</TableCell></TableRow>}
            {!loading && contacts.map((c) => (
              <TableRow key={c.id} data-testid={`admin-contact-row-${c.id}`}>
                <TableCell className="text-sm text-[hsl(var(--muted-foreground))]">{new Date(c.created_at).toLocaleString("pt-BR")}</TableCell>
                <TableCell className="font-medium">{c.name}</TableCell>
                <TableCell className="hidden md:table-cell text-sm">{c.phone}</TableCell>
                <TableCell className="max-w-[380px] text-sm">{c.message}</TableCell>
                <TableCell className="text-right">
                  {c.whatsapp_sent
                    ? <Badge className="rounded-full bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))] border-transparent">Enviado</Badge>
                    : <Badge variant="outline" className="rounded-full">Pendente</Badge>}
                </TableCell>
              </TableRow>
            ))}
            {!loading && contacts.length === 0 && (
              <TableRow><TableCell colSpan={5} className="py-10 text-center text-[hsl(var(--muted-foreground))]">Nenhum contato ainda.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}


function RightsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ title: "", category: "", summary: "", content: "", order: 0, published: true });
  const [regenSlug, setRegenSlug] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/admin/rights").then(({ data }) => setItems(data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ title: "", category: "", summary: "", content: "", order: items.length + 1, published: true });
    setDialogOpen(true);
  };
  const openEdit = (r) => {
    setEditing(r);
    setForm({
      title: r.title || "", category: r.category || "", summary: r.summary || "",
      content: r.content || "", order: r.order || 0, published: r.published,
    });
    setDialogOpen(true);
  };
  const save = async () => {
    try {
      if (editing) await api.put(`/admin/rights/${editing.id}`, form);
      else await api.post("/admin/rights", form);
      toast.success(editing ? "Tópico atualizado." : "Tópico criado.");
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao salvar.");
    }
  };
  const remove = async (r) => {
    if (!window.confirm(`Excluir o tópico "${r.title}"? Esta ação não pode ser desfeita.`)) return;
    try {
      await api.delete(`/admin/rights/${r.id}`);
      toast.success("Tópico excluído.");
      load();
    } catch (_) { toast.error("Falha ao excluir."); }
  };
  const togglePublish = async (r) => {
    try {
      await api.put(`/admin/rights/${r.id}`, { published: !r.published });
      load();
    } catch (_) { toast.error("Falha ao alterar status."); }
  };
  const regenerate = async (r) => {
    if (!window.confirm(`Regenerar o conteúdo de "${r.title}" via IA?\n\nO conteúdo atual será substituído.`)) return;
    setRegenSlug(r.slug);
    try {
      await api.post(`/admin/rights/regenerate/${r.slug}`);
      toast.success("Conteúdo regenerado.");
      load();
    } catch (_) { toast.error("Falha ao regenerar (LLM indisponível)."); }
    finally { setRegenSlug(null); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-[hsl(var(--secondary))]">Área de Direitos</div>
          <h2 className="font-heading text-2xl font-black">Tópicos publicados na área /direitos</h2>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
            Gerencie o conteúdo público. Você pode editar, criar novos tópicos, ocultar ou regenerar via IA.
          </p>
        </div>
        <Button onClick={openCreate} className="rounded-full bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90" data-testid="rights-admin-new-btn">
          <Plus className="h-4 w-4 mr-1.5" /> Novo tópico
        </Button>
      </div>

      <div className="surface p-2 sm:p-4 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Título</TableHead>
              <TableHead>Categoria</TableHead>
              <TableHead className="text-center">Ordem</TableHead>
              <TableHead className="text-center">Status</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={5} className="py-10 text-center text-[hsl(var(--muted-foreground))]">Carregando…</TableCell></TableRow>
            )}
            {!loading && items.length === 0 && (
              <TableRow><TableCell colSpan={5} className="py-10 text-center text-[hsl(var(--muted-foreground))]">Nenhum tópico ainda. Aguarde a IA gerar os padrões ou crie um novo.</TableCell></TableRow>
            )}
            {items.map((r) => (
              <TableRow key={r.id} data-testid={`rights-admin-row-${r.slug}`}>
                <TableCell className="font-medium">
                  <a href={`/direitos/${r.slug}`} target="_blank" rel="noreferrer" className="hover:text-[hsl(var(--primary))]">
                    {r.title}
                  </a>
                  <div className="text-xs text-[hsl(var(--muted-foreground))] line-clamp-1 mt-0.5">{r.summary}</div>
                </TableCell>
                <TableCell><Badge variant="outline" className="rounded-full">{r.category}</Badge></TableCell>
                <TableCell className="text-center">{r.order}</TableCell>
                <TableCell className="text-center">
                  <button onClick={() => togglePublish(r)} data-testid={`rights-admin-toggle-${r.slug}`} className="inline-flex">
                    {r.published
                      ? <ToggleRight className="h-6 w-6 text-[hsl(var(--primary))]" />
                      : <ToggleLeft className="h-6 w-6 text-[hsl(var(--muted-foreground))]" />}
                  </button>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button variant="ghost" size="icon" title="Regenerar via IA" disabled={regenSlug === r.slug}
                      onClick={() => regenerate(r)} className="rounded-full h-8 w-8"
                      data-testid={`rights-admin-regen-${r.slug}`}>
                      <Sparkles className={`h-4 w-4 ${regenSlug === r.slug ? "animate-pulse" : ""}`} />
                    </Button>
                    <Button variant="ghost" size="icon" title="Editar"
                      onClick={() => openEdit(r)} className="rounded-full h-8 w-8"
                      data-testid={`rights-admin-edit-${r.slug}`}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" title="Excluir"
                      onClick={() => remove(r)} className="rounded-full h-8 w-8 text-[hsl(var(--destructive))]"
                      data-testid={`rights-admin-delete-${r.slug}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="rights-admin-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Editar tópico" : "Novo tópico"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="r-title">Título</Label>
              <Input id="r-title" value={form.title} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))} data-testid="rights-admin-title-input" />
            </div>
            <div className="grid sm:grid-cols-[1fr_120px] gap-4">
              <div>
                <Label htmlFor="r-cat">Categoria</Label>
                <Input id="r-cat" value={form.category} onChange={(e) => setForm((p) => ({ ...p, category: e.target.value }))} placeholder="Ex.: Previdência" data-testid="rights-admin-category-input" />
              </div>
              <div>
                <Label htmlFor="r-ord">Ordem</Label>
                <Input id="r-ord" type="number" value={form.order} onChange={(e) => setForm((p) => ({ ...p, order: Number(e.target.value) }))} data-testid="rights-admin-order-input" />
              </div>
            </div>
            <div>
              <Label htmlFor="r-sum">Resumo curto (exibido no card)</Label>
              <Textarea id="r-sum" rows={2} value={form.summary} onChange={(e) => setForm((p) => ({ ...p, summary: e.target.value }))} data-testid="rights-admin-summary-input" />
            </div>
            <div>
              <Label htmlFor="r-body">Conteúdo (Markdown)</Label>
              <Textarea id="r-body" rows={12} value={form.content} onChange={(e) => setForm((p) => ({ ...p, content: e.target.value }))} className="font-mono text-sm" data-testid="rights-admin-content-input" />
              <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
                Use Markdown. Ex.: <code>## Título</code>, <code>**negrito**</code>, listas com <code>-</code> ou <code>1.</code>.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <input id="r-pub" type="checkbox" checked={!!form.published} onChange={(e) => setForm((p) => ({ ...p, published: e.target.checked }))} data-testid="rights-admin-published-checkbox" />
              <Label htmlFor="r-pub" className="cursor-pointer">Publicar (visível ao público)</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-full">Cancelar</Button>
            <Button onClick={save} className="rounded-full bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90" data-testid="rights-admin-save-btn">
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------------- Diagnostic Guide Tab ----------------------
function DiagnosticGuideTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ title: "", category: "", summary: "", content: "", order: 0, published: true });
  const [regenSlug, setRegenSlug] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/admin/diagnostic-guide").then(({ data }) => setItems(data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ title: "", category: "", summary: "", content: "", order: items.length + 1, published: true });
    setDialogOpen(true);
  };
  const openEdit = (r) => {
    setEditing(r);
    setForm({
      title: r.title || "", category: r.category || "", summary: r.summary || "",
      content: r.content || "", order: r.order || 0, published: r.published,
    });
    setDialogOpen(true);
  };
  const save = async () => {
    try {
      if (editing) await api.put(`/admin/diagnostic-guide/${editing.id}`, form);
      else await api.post("/admin/diagnostic-guide", form);
      toast.success(editing ? "Tópico atualizado." : "Tópico criado.");
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao salvar.");
    }
  };
  const remove = async (r) => {
    if (!window.confirm(`Excluir o tópico "${r.title}"? Esta ação não pode ser desfeita.`)) return;
    try {
      await api.delete(`/admin/diagnostic-guide/${r.id}`);
      toast.success("Tópico excluído.");
      load();
    } catch (_) { toast.error("Falha ao excluir."); }
  };
  const togglePublish = async (r) => {
    try {
      await api.put(`/admin/diagnostic-guide/${r.id}`, { published: !r.published });
      load();
    } catch (_) { toast.error("Falha ao alterar status."); }
  };
  const regenerate = async (r) => {
    if (!window.confirm(`Regenerar o conteúdo de "${r.title}" via IA?\n\nO conteúdo atual será substituído.\nEste processo pode levar até 1 minuto.`)) return;
    setRegenSlug(r.slug);
    try {
      await api.post(`/admin/diagnostic-guide/regenerate/${r.slug}`);
      toast.success("Conteúdo regenerado.");
      load();
    } catch (_) { toast.error("Falha ao regenerar (LLM indisponível). Tente novamente."); }
    finally { setRegenSlug(null); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-[hsl(var(--secondary))]">Guia do Diagnóstico</div>
          <h2 className="font-heading text-2xl font-black">Tópicos publicados em /guia-do-diagnostico</h2>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
            Gerencie os tópicos do Guia do Diagnóstico. Você pode editar, criar, ocultar ou regenerar via IA.
          </p>
        </div>
        <Button onClick={openCreate} className="rounded-full bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90" data-testid="diagnostic-admin-new-btn">
          <Plus className="h-4 w-4 mr-1.5" /> Novo tópico
        </Button>
      </div>

      <div className="surface p-2 sm:p-4 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Título</TableHead>
              <TableHead>Categoria</TableHead>
              <TableHead className="text-center">Ordem</TableHead>
              <TableHead className="text-center">Status</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={5} className="py-10 text-center text-[hsl(var(--muted-foreground))]">Carregando…</TableCell></TableRow>
            )}
            {!loading && items.length === 0 && (
              <TableRow><TableCell colSpan={5} className="py-10 text-center text-[hsl(var(--muted-foreground))]">Nenhum tópico ainda. Aguarde a IA gerar os padrões ou crie um novo.</TableCell></TableRow>
            )}
            {items.map((r) => (
              <TableRow key={r.id} data-testid={`diagnostic-admin-row-${r.slug}`}>
                <TableCell className="font-medium">
                  <a href={`/guia-do-diagnostico/${r.slug}`} target="_blank" rel="noreferrer" className="hover:text-[hsl(var(--primary))]">
                    {r.title}
                  </a>
                  <div className="text-xs text-[hsl(var(--muted-foreground))] line-clamp-1 mt-0.5">{r.summary}</div>
                </TableCell>
                <TableCell><Badge variant="outline" className="rounded-full">{r.category}</Badge></TableCell>
                <TableCell className="text-center">{r.order}</TableCell>
                <TableCell className="text-center">
                  <button onClick={() => togglePublish(r)} data-testid={`diagnostic-admin-toggle-${r.slug}`} className="inline-flex">
                    {r.published
                      ? <ToggleRight className="h-6 w-6 text-[hsl(var(--primary))]" />
                      : <ToggleLeft className="h-6 w-6 text-[hsl(var(--muted-foreground))]" />}
                  </button>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button variant="ghost" size="icon" title="Regenerar via IA" disabled={regenSlug === r.slug}
                      onClick={() => regenerate(r)} className="rounded-full h-8 w-8"
                      data-testid={`diagnostic-admin-regen-${r.slug}`}>
                      <Sparkles className={`h-4 w-4 ${regenSlug === r.slug ? "animate-pulse" : ""}`} />
                    </Button>
                    <Button variant="ghost" size="icon" title="Editar"
                      onClick={() => openEdit(r)} className="rounded-full h-8 w-8"
                      data-testid={`diagnostic-admin-edit-${r.slug}`}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" title="Excluir"
                      onClick={() => remove(r)} className="rounded-full h-8 w-8 text-[hsl(var(--destructive))]"
                      data-testid={`diagnostic-admin-delete-${r.slug}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="diagnostic-admin-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Editar tópico" : "Novo tópico"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="d-title">Título</Label>
              <Input id="d-title" value={form.title} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))} data-testid="diagnostic-admin-title-input" />
            </div>
            <div className="grid sm:grid-cols-[1fr_120px] gap-4">
              <div>
                <Label htmlFor="d-cat">Categoria</Label>
                <Input id="d-cat" value={form.category} onChange={(e) => setForm((p) => ({ ...p, category: e.target.value }))} placeholder="Ex.: Profissionais" data-testid="diagnostic-admin-category-input" />
                <p className="text-[10px] text-[hsl(var(--muted-foreground))] mt-1">
                  Sugestões: <span className="font-mono">Sinais e Observação</span>, <span className="font-mono">Onde começar</span>, <span className="font-mono">Profissionais</span>, <span className="font-mono">Exames e avaliações</span>, <span className="font-mono">Após o diagnóstico</span>
                </p>
              </div>
              <div>
                <Label htmlFor="d-ord">Ordem</Label>
                <Input id="d-ord" type="number" value={form.order} onChange={(e) => setForm((p) => ({ ...p, order: Number(e.target.value) }))} data-testid="diagnostic-admin-order-input" />
              </div>
            </div>
            <div>
              <Label htmlFor="d-sum">Resumo curto (exibido no card)</Label>
              <Textarea id="d-sum" rows={2} value={form.summary} onChange={(e) => setForm((p) => ({ ...p, summary: e.target.value }))} data-testid="diagnostic-admin-summary-input" />
            </div>
            <div>
              <Label htmlFor="d-body">Conteúdo (Markdown)</Label>
              <Textarea id="d-body" rows={12} value={form.content} onChange={(e) => setForm((p) => ({ ...p, content: e.target.value }))} className="font-mono text-sm" data-testid="diagnostic-admin-content-input" />
              <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
                Use Markdown. Ex.: <code>## Título</code>, <code>**negrito**</code>, listas com <code>-</code> ou <code>1.</code>.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <input id="d-pub" type="checkbox" checked={!!form.published} onChange={(e) => setForm((p) => ({ ...p, published: e.target.checked }))} data-testid="diagnostic-admin-published-checkbox" />
              <Label htmlFor="d-pub" className="cursor-pointer">Publicar (visível ao público)</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-full">Cancelar</Button>
            <Button onClick={save} className="rounded-full bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90" data-testid="diagnostic-admin-save-btn">
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------------- WhatsApp Tab ----------------------
function WhatsAppTab() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [resetting, setResetting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/whatsapp/sessions");
      setSessions(data);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30000); // atualiza a cada 30s
    return () => clearInterval(t);
  }, []);

  const openDetail = async (s) => {
    setSelected(s);
    setDetail(null);
    try {
      const { data } = await api.get(`/admin/whatsapp/sessions/${s.phone}`);
      setDetail(data);
    } catch { setDetail({ history: [] }); }
  };

  const returnToBot = async (phone) => {
    if (!window.confirm("Devolver esta conversa ao robô? A pessoa voltará a ser respondida automaticamente.")) return;
    setResetting(true);
    try {
      await api.post(`/admin/whatsapp/sessions/${phone}/reset`, { status: "bot" });
      toast.success("Conversa devolvida ao robô.");
      await load();
      if (selected?.phone === phone) await openDetail({ ...selected, status: "bot" });
    } catch (e) { toast.error(e.response?.data?.detail || "Falha ao resetar."); }
    finally { setResetting(false); }
  };

  const closeSession = async (phone) => {
    if (!window.confirm("Encerrar esta conversa? O robô só volta a atender se a pessoa mandar uma nova mensagem.")) return;
    setResetting(true);
    try {
      await api.post(`/admin/whatsapp/sessions/${phone}/reset`, { status: "closed" });
      toast.success("Conversa encerrada.");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Falha ao encerrar."); }
    finally { setResetting(false); }
  };

  const relTime = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 60000;
    if (diff < 1) return "agora";
    if (diff < 60) return `${Math.round(diff)} min atrás`;
    if (diff < 60 * 24) return `${Math.round(diff / 60)} h atrás`;
    return `${Math.round(diff / (60 * 24))} d atrás`;
  };

  const statusBadge = (status) => {
    if (status === "human") {
      return <Badge className="rounded-full bg-amber-100 text-amber-800 border-transparent hover:bg-amber-100"><User className="h-3 w-3 mr-1" /> Humano</Badge>;
    }
    if (status === "closed") {
      return <Badge className="rounded-full bg-slate-100 text-slate-700 border-transparent hover:bg-slate-100">Encerrada</Badge>;
    }
    return <Badge className="rounded-full bg-emerald-100 text-emerald-800 border-transparent hover:bg-emerald-100"><Bot className="h-3 w-3 mr-1" /> Robô</Badge>;
  };

  const botCount = sessions.filter((s) => s.status === "bot").length;
  const humanCount = sessions.filter((s) => s.status === "human").length;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-[hsl(var(--secondary))]">Robô + Atendimento humano</div>
          <h2 className="font-heading text-2xl font-black">Conversas do WhatsApp</h2>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
            Quando você responde uma família pelo próprio WhatsApp, o robô pausa automaticamente. Depois de 10 minutos sem qualquer mensagem, o robô volta a atender no próximo contato.
          </p>
        </div>
        <Button variant="outline" onClick={load} className="rounded-full" data-testid="wa-refresh-btn">
          <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Atualizar
        </Button>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <div className="surface p-4"><div className="text-xs text-[hsl(var(--muted-foreground))]">Total de conversas</div><div className="font-heading text-2xl font-black">{sessions.length}</div></div>
        <div className="surface p-4"><div className="text-xs text-[hsl(var(--muted-foreground))]">Robô ativo</div><div className="font-heading text-2xl font-black text-emerald-700">{botCount}</div></div>
        <div className="surface p-4"><div className="text-xs text-[hsl(var(--muted-foreground))]">Você atendendo</div><div className="font-heading text-2xl font-black text-amber-700">{humanCount}</div></div>
      </div>

      <div className="surface p-2 sm:p-4 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Contato</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-center">Msgs</TableHead>
              <TableHead>Última msg</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && sessions.length === 0 && (
              <TableRow><TableCell colSpan={5} className="py-10 text-center text-[hsl(var(--muted-foreground))]">Carregando…</TableCell></TableRow>
            )}
            {!loading && sessions.length === 0 && (
              <TableRow><TableCell colSpan={5} className="py-10 text-center text-[hsl(var(--muted-foreground))]">Ainda não há conversas. Assim que uma família mandar mensagem no WhatsApp do instituto, aparecerá aqui.</TableCell></TableRow>
            )}
            {sessions.map((s) => (
              <TableRow key={s.phone} data-testid={`wa-row-${s.phone}`}>
                <TableCell>
                  <div className="font-medium">{s.sender_name || "—"}</div>
                  <div className="text-xs text-[hsl(var(--muted-foreground))] font-mono">{s.phone}</div>
                </TableCell>
                <TableCell>{statusBadge(s.status)}</TableCell>
                <TableCell className="text-center">{s.message_count}</TableCell>
                <TableCell className="text-sm text-[hsl(var(--muted-foreground))]">{relTime(s.last_message_at)}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button variant="ghost" size="sm" onClick={() => openDetail(s)} data-testid={`wa-view-${s.phone}`} className="rounded-full">
                      <Eye className="h-4 w-4 mr-1" /> Ver
                    </Button>
                    {s.status === "human" && (
                      <Button variant="outline" size="sm" onClick={() => returnToBot(s.phone)} disabled={resetting} data-testid={`wa-return-bot-${s.phone}`} className="rounded-full">
                        <Bot className="h-4 w-4 mr-1" /> Devolver ao robô
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => { if (!o) { setSelected(null); setDetail(null); } }}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="wa-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              {selected?.sender_name || selected?.phone}
              {selected && statusBadge(selected.status)}
            </DialogTitle>
            <div className="text-xs text-[hsl(var(--muted-foreground))] font-mono">{selected?.phone}</div>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto -mx-6 px-6 py-2 space-y-3 bg-[hsl(var(--muted))]/40">
            {!detail && <div className="text-center py-8 text-sm text-[hsl(var(--muted-foreground))]">Carregando conversa…</div>}
            {detail && detail.history?.length === 0 && <div className="text-center py-8 text-sm text-[hsl(var(--muted-foreground))]">Sem histórico.</div>}
            {detail?.history?.map((m, i) => {
              const isAssistant = m.role === "assistant";
              const isAdmin = m.role === "admin";
              const isUser = m.role === "user";
              return (
                <div key={i} className={`flex ${isUser ? "justify-start" : "justify-end"}`}>
                  <div className={`max-w-[80%] rounded-2xl px-4 py-2 text-sm shadow-sm ${
                    isUser ? "bg-white border border-[hsl(var(--border))]"
                    : isAdmin ? "bg-amber-100 text-amber-950 border border-amber-200"
                    : "bg-[#25D366] text-white"
                  }`}>
                    <div className="text-[10px] font-bold uppercase tracking-widest opacity-70 mb-1">
                      {isUser ? "Família" : isAdmin ? "Equipe (você)" : "Robô"}
                    </div>
                    <div className="whitespace-pre-wrap">{m.text}</div>
                    {m.at && <div className="text-[10px] opacity-60 mt-1">{new Date(m.at).toLocaleString("pt-BR")}</div>}
                  </div>
                </div>
              );
            })}
          </div>

          <DialogFooter className="flex-wrap gap-2">
            {selected && (
              <>
                {selected.status === "human" && (
                  <Button onClick={() => returnToBot(selected.phone)} disabled={resetting} className="rounded-full bg-[#25D366] text-white hover:bg-[#25D366]/90" data-testid="wa-detail-return-bot">
                    <Bot className="h-4 w-4 mr-1.5" /> Devolver ao robô
                  </Button>
                )}
                {selected.status !== "closed" && (
                  <Button variant="outline" onClick={() => closeSession(selected.phone)} disabled={resetting} className="rounded-full" data-testid="wa-detail-close">
                    Encerrar conversa
                  </Button>
                )}
                <Button variant="outline" onClick={() => { setSelected(null); setDetail(null); }} className="rounded-full">Fechar</Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CoursesTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [courseDialog, setCourseDialog] = useState(false);
  const [courseForm, setCourseForm] = useState({ title: "", description: "", category: "", level: "iniciante", estimated_hours: 0, cover_image_url: "", order: 0, published: true });

  const [selectedCourse, setSelectedCourse] = useState(null);
  const [moduleDialog, setModuleDialog] = useState(false);
  const [modForm, setModForm] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/admin/courses").then(({ data }) => setItems(data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const openCreateCourse = () => {
    setEditing(null);
    setCourseForm({ title: "", description: "", category: "", level: "iniciante", estimated_hours: 0, cover_image_url: "", order: items.length + 1, published: true });
    setCourseDialog(true);
  };
  const openEditCourse = (c) => {
    setEditing(c);
    setCourseForm({
      title: c.title, description: c.description || "", category: c.category || "",
      level: c.level || "iniciante", estimated_hours: c.estimated_hours || 0,
      cover_image_url: c.cover_image_url || "",
      order: c.order || 0, published: c.published,
    });
    setCourseDialog(true);
  };
  const saveCourse = async () => {
    try {
      if (editing) await api.put(`/admin/courses/${editing.id}`, courseForm);
      else await api.post("/admin/courses", courseForm);
      toast.success(editing ? "Curso atualizado." : "Curso criado.");
      setCourseDialog(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao salvar."); }
  };
  const deleteCourse = async (c) => {
    if (!window.confirm(`Excluir o curso "${c.title}" e TODAS as matrículas? Não pode ser desfeito.`)) return;
    try { await api.delete(`/admin/courses/${c.id}`); toast.success("Curso excluído."); load(); }
    catch (_) { toast.error("Falha ao excluir."); }
  };
  const togglePublish = async (c) => {
    try { await api.put(`/admin/courses/${c.id}`, { published: !c.published }); load(); }
    catch (_) { toast.error("Falha."); }
  };

  const openAddModule = (course) => {
    setSelectedCourse(course);
    setModForm({
      title: "", description: "", content: "", video_url: "", order: (course.modules?.length || 0) + 1,
      quiz_pass_score: 70, quiz_questions: [],
    });
    setModuleDialog(true);
  };
  const openEditModule = (course, module) => {
    setSelectedCourse(course);
    setModForm({
      id: module.id, order: module.order, title: module.title, description: module.description || "",
      content: module.content || "", video_url: module.video_url || "",
      quiz_pass_score: module.quiz?.pass_score || 70,
      quiz_questions: (module.quiz?.questions || []).map((q) => ({
        id: q.id, prompt: q.prompt, options: q.options || ["", "", "", ""],
        correct_index: q.correct_index || 0, explanation: q.explanation || "",
      })),
    });
    setModuleDialog(true);
  };
  const saveModule = async () => {
    if (!selectedCourse || !modForm) return;
    try {
      const payload = { ...modForm };
      if (modForm.id) await api.put(`/admin/courses/${selectedCourse.id}/modules/${modForm.id}`, payload);
      else await api.post(`/admin/courses/${selectedCourse.id}/modules`, payload);
      toast.success("Módulo salvo.");
      setModuleDialog(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao salvar."); }
  };
  const deleteModule = async (course, module) => {
    if (!window.confirm(`Excluir o módulo "${module.title}"?`)) return;
    try { await api.delete(`/admin/courses/${course.id}/modules/${module.id}`); toast.success("Módulo excluído."); load(); }
    catch (_) { toast.error("Falha."); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-[hsl(var(--secondary))]">Cuidar Educacional</div>
          <h2 className="font-heading text-2xl font-black">Cursos, módulos e quizzes</h2>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
            Gerencie a plataforma de cursos. Adicione vídeos do YouTube, conteúdo em Markdown e quizzes com múltipla escolha.
          </p>
        </div>
        <Button onClick={openCreateCourse} className="rounded-full bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90" data-testid="courses-admin-new-btn">
          <Plus className="h-4 w-4 mr-1.5" /> Novo curso
        </Button>
      </div>

      {loading && <div className="surface p-10 text-center text-sm text-[hsl(var(--muted-foreground))]">Carregando…</div>}
      {!loading && items.length === 0 && <div className="surface p-10 text-center text-sm text-[hsl(var(--muted-foreground))]">Nenhum curso ainda. Aguarde a IA gerar os padrões ou crie um novo.</div>}

      {items.map((c) => (
        <div key={c.id} className="surface p-6" data-testid={`courses-admin-course-${c.slug}`}>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="rounded-full">{c.category}</Badge>
                <Badge variant="outline" className="rounded-full text-xs">{c.level}</Badge>
              </div>
              <div className="mt-2 font-heading font-black text-lg">{c.title}</div>
              <p className="text-sm text-[hsl(var(--muted-foreground))] line-clamp-2 mt-1">{c.description}</p>
              <div className="text-xs text-[hsl(var(--muted-foreground))] mt-2 flex flex-wrap gap-3">
                <span>{c.modules?.length || 0} módulos</span>
                <span>Ordem: {c.order}</span>
                <span className="inline-flex items-center gap-1">
                  {c.published
                    ? <><ToggleRight className="h-4 w-4 text-[hsl(var(--primary))]" /> Publicado</>
                    : <><ToggleLeft className="h-4 w-4" /> Rascunho</>}
                </span>
              </div>
            </div>
            <div className="flex gap-1 shrink-0">
              <Button variant="ghost" size="icon" onClick={() => togglePublish(c)} title="Publicar/despublicar" className="rounded-full h-8 w-8" data-testid={`courses-admin-toggle-${c.slug}`}>
                {c.published ? <ToggleRight className="h-4 w-4" /> : <ToggleLeft className="h-4 w-4" />}
              </Button>
              <Button variant="ghost" size="icon" onClick={() => openEditCourse(c)} title="Editar curso" className="rounded-full h-8 w-8" data-testid={`courses-admin-edit-${c.slug}`}>
                <Pencil className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" onClick={() => deleteCourse(c)} title="Excluir" className="rounded-full h-8 w-8 text-[hsl(var(--destructive))]" data-testid={`courses-admin-delete-${c.slug}`}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <div className="mt-5 border-t border-[hsl(var(--border))] pt-4">
            <div className="flex items-center justify-between">
              <div className="font-heading font-bold text-sm">Módulos</div>
              <Button variant="outline" size="sm" onClick={() => openAddModule(c)} className="rounded-full h-8" data-testid={`courses-admin-add-mod-${c.slug}`}>
                <Plus className="h-3 w-3 mr-1" /> Novo módulo
              </Button>
            </div>
            <div className="mt-3 space-y-2">
              {(c.modules || []).map((m) => (
                <div key={m.id} className="flex items-center gap-3 rounded-xl border border-[hsl(var(--border))] p-3" data-testid={`courses-admin-mod-${m.id}`}>
                  <div className="h-8 w-8 rounded-full bg-[hsl(var(--muted))] grid place-items-center text-xs font-heading font-black">{m.order}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">{m.title}</div>
                    <div className="text-xs text-[hsl(var(--muted-foreground))] flex flex-wrap gap-2 mt-0.5">
                      {m.video_url ? <span className="inline-flex items-center gap-1"><Youtube className="h-3 w-3" /> vídeo</span> : <span>sem vídeo</span>}
                      <span>{m.quiz?.questions?.length || 0} perguntas</span>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEditModule(c, m)} data-testid={`courses-admin-mod-edit-${m.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-[hsl(var(--destructive))]" onClick={() => deleteModule(c, m)} data-testid={`courses-admin-mod-delete-${m.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                </div>
              ))}
              {(!c.modules || c.modules.length === 0) && <div className="text-xs text-[hsl(var(--muted-foreground))]">Nenhum módulo neste curso ainda.</div>}
            </div>
          </div>
        </div>
      ))}

      {/* Course dialog */}
      <Dialog open={courseDialog} onOpenChange={setCourseDialog}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Editar curso" : "Novo curso"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Título</Label>
              <Input value={courseForm.title} onChange={(e) => setCourseForm((p) => ({ ...p, title: e.target.value }))} data-testid="courses-admin-title-input" />
            </div>
            <div>
              <Label>Descrição</Label>
              <Textarea rows={3} value={courseForm.description} onChange={(e) => setCourseForm((p) => ({ ...p, description: e.target.value }))} data-testid="courses-admin-desc-input" />
            </div>
            <div>
              <Label>URL da imagem de capa</Label>
              <Input placeholder="https://... (Unsplash, seu servidor, etc.)" value={courseForm.cover_image_url || ""} onChange={(e) => setCourseForm((p) => ({ ...p, cover_image_url: e.target.value }))} data-testid="courses-admin-cover-input" />
              {courseForm.cover_image_url && (
                <div className="mt-2 h-24 w-full rounded-lg overflow-hidden bg-[hsl(var(--muted))]">
                  <img src={courseForm.cover_image_url} alt="Prévia" className="w-full h-full object-cover" />
                </div>
              )}
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label>Categoria</Label><Input value={courseForm.category} onChange={(e) => setCourseForm((p) => ({ ...p, category: e.target.value }))} /></div>
              <div><Label>Nível</Label>
                <Select value={courseForm.level} onValueChange={(v) => setCourseForm((p) => ({ ...p, level: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="iniciante">Iniciante</SelectItem>
                    <SelectItem value="intermediario">Intermediário</SelectItem>
                    <SelectItem value="avancado">Avançado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label>Duração (horas)</Label><Input type="number" min="0" step="0.5" value={courseForm.estimated_hours} onChange={(e) => setCourseForm((p) => ({ ...p, estimated_hours: Number(e.target.value) }))} /></div>
              <div><Label>Ordem</Label><Input type="number" value={courseForm.order} onChange={(e) => setCourseForm((p) => ({ ...p, order: Number(e.target.value) }))} /></div>
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" checked={!!courseForm.published} onChange={(e) => setCourseForm((p) => ({ ...p, published: e.target.checked }))} />
              <Label>Publicado</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCourseDialog(false)} className="rounded-full">Cancelar</Button>
            <Button onClick={saveCourse} className="rounded-full bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90" data-testid="courses-admin-save-btn">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Module dialog */}
      <Dialog open={moduleDialog} onOpenChange={setModuleDialog}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="courses-admin-module-dialog">
          <DialogHeader><DialogTitle className="font-heading">{modForm?.id ? "Editar módulo" : "Novo módulo"}</DialogTitle></DialogHeader>
          {modForm && (
            <div className="space-y-4">
              <div className="grid sm:grid-cols-[1fr_120px] gap-4">
                <div><Label>Título</Label><Input value={modForm.title} onChange={(e) => setModForm((p) => ({ ...p, title: e.target.value }))} data-testid="mod-title-input" /></div>
                <div><Label>Ordem</Label><Input type="number" value={modForm.order} onChange={(e) => setModForm((p) => ({ ...p, order: Number(e.target.value) }))} /></div>
              </div>
              <div><Label>Descrição curta</Label><Input value={modForm.description} onChange={(e) => setModForm((p) => ({ ...p, description: e.target.value }))} /></div>
              <div><Label>URL do YouTube</Label><Input placeholder="https://www.youtube.com/watch?v=..." value={modForm.video_url} onChange={(e) => setModForm((p) => ({ ...p, video_url: e.target.value }))} data-testid="mod-video-input" /></div>
              <div>
                <Label>Conteúdo da aula (Markdown)</Label>
                <Textarea rows={8} className="font-mono text-sm" value={modForm.content} onChange={(e) => setModForm((p) => ({ ...p, content: e.target.value }))} data-testid="mod-content-input" />
              </div>
              <div className="border-t border-[hsl(var(--border))] pt-4">
                <div className="flex items-center justify-between">
                  <div className="font-heading font-bold flex items-center gap-2"><ListChecks className="h-4 w-4 text-[hsl(var(--secondary))]" /> Quiz</div>
                  <Button variant="outline" size="sm" className="rounded-full h-8" onClick={() => setModForm((p) => ({
                    ...p, quiz_questions: [...(p.quiz_questions || []), { prompt: "", options: ["", "", "", ""], correct_index: 0, explanation: "" }],
                  }))}>
                    <Plus className="h-3 w-3 mr-1" /> Pergunta
                  </Button>
                </div>
                <div className="mt-3 grid sm:grid-cols-[120px_1fr] gap-4">
                  <div><Label>Nota mínima (%)</Label><Input type="number" min="0" max="100" value={modForm.quiz_pass_score} onChange={(e) => setModForm((p) => ({ ...p, quiz_pass_score: Number(e.target.value) }))} /></div>
                </div>
                <div className="mt-4 space-y-4">
                  {(modForm.quiz_questions || []).map((q, qi) => (
                    <div key={qi} className="rounded-xl border border-[hsl(var(--border))] p-4 space-y-2">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1"><Label>Pergunta {qi + 1}</Label>
                          <Input value={q.prompt} onChange={(e) => setModForm((p) => ({
                            ...p, quiz_questions: p.quiz_questions.map((x, i) => i === qi ? { ...x, prompt: e.target.value } : x),
                          }))} />
                        </div>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-[hsl(var(--destructive))]" onClick={() => setModForm((p) => ({
                          ...p, quiz_questions: p.quiz_questions.filter((_, i) => i !== qi),
                        }))}><Trash2 className="h-3.5 w-3.5" /></Button>
                      </div>
                      <div className="grid gap-2">
                        {q.options.map((opt, oi) => (
                          <div key={oi} className="flex items-center gap-2">
                            <input type="radio" checked={q.correct_index === oi} onChange={() => setModForm((p) => ({
                              ...p, quiz_questions: p.quiz_questions.map((x, i) => i === qi ? { ...x, correct_index: oi } : x),
                            }))} title="Correta" />
                            <Input placeholder={`Opção ${String.fromCharCode(65 + oi)}`} value={opt} onChange={(e) => setModForm((p) => ({
                              ...p, quiz_questions: p.quiz_questions.map((x, i) => i === qi ? {
                                ...x, options: x.options.map((o, j) => j === oi ? e.target.value : o),
                              } : x),
                            }))} />
                          </div>
                        ))}
                      </div>
                      <Input placeholder="Explicação (opcional)" value={q.explanation} onChange={(e) => setModForm((p) => ({
                        ...p, quiz_questions: p.quiz_questions.map((x, i) => i === qi ? { ...x, explanation: e.target.value } : x),
                      }))} />
                    </div>
                  ))}
                  {(!modForm.quiz_questions || modForm.quiz_questions.length === 0) && (
                    <div className="text-xs text-[hsl(var(--muted-foreground))]">Nenhuma pergunta ainda — o módulo será marcado como concluído após o aluno abrir a aula.</div>
                  )}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setModuleDialog(false)} className="rounded-full">Cancelar</Button>
            <Button onClick={saveModule} className="rounded-full bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90" data-testid="mod-save-btn">Salvar módulo</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ---------------------- Supporters Tab ----------------------
function SupportersTab() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total: 0, volunteers: 0, donors_declared: 0, companies: 0 });
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q.trim()) params.q = q.trim();
      if (filter && filter !== "all") params.filter = filter;
      const [{ data: list }, { data: st }] = await Promise.all([
        api.get("/admin/supporters", { params }),
        api.get("/admin/supporters/stats"),
      ]);
      setItems(list); setStats(st);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [filter]);

  const digitsOnly = (s) => (s || "").replace(/\D/g, "");
  const openWA = (phone, name) => {
    const p = digitsOnly(phone);
    if (!p) return;
    const first = (name || "").trim().split(" ")[0];
    const text = encodeURIComponent(`Oi${first ? " " + first : ""}! Aqui é da equipe do Instituto Cuidar 🤗`);
    window.open(`https://wa.me/${p}?text=${text}`, "_blank");
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-[hsl(var(--secondary))]">Apoiadores</div>
          <h2 className="font-heading text-2xl font-black">Voluntários, doadores e empresas parceiras</h2>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
            Encontre e entre em contato com quem apoia o Instituto Cuidar. Use os filtros e a busca por nome, e-mail, telefone ou CNPJ.
          </p>
        </div>
        <Button variant="outline" onClick={load} className="rounded-full" data-testid="supporters-refresh-btn">
          <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Atualizar
        </Button>
      </div>

      <div className="grid gap-3 sm:grid-cols-4">
        <div className="surface p-4"><div className="text-xs text-[hsl(var(--muted-foreground))]">Total</div><div className="font-heading text-2xl font-black">{stats.total}</div></div>
        <div className="surface p-4"><div className="text-xs text-[hsl(var(--muted-foreground))]">Voluntários</div><div className="font-heading text-2xl font-black text-emerald-700">{stats.volunteers}</div></div>
        <div className="surface p-4"><div className="text-xs text-[hsl(var(--muted-foreground))]">Doadores</div><div className="font-heading text-2xl font-black text-rose-700">{stats.donors_declared}</div></div>
        <div className="surface p-4"><div className="text-xs text-[hsl(var(--muted-foreground))]">Empresas</div><div className="font-heading text-2xl font-black text-sky-700">{stats.companies}</div></div>
      </div>

      <div className="flex gap-3 flex-wrap items-center">
        <Input
          placeholder="Buscar por nome, e-mail, telefone, empresa ou CNPJ…"
          value={q} onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && load()}
          className="max-w-md" data-testid="supporters-search-input"
        />
        <Button onClick={load} className="btn-pill bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90">Buscar</Button>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-[200px]" data-testid="supporters-filter-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="volunteer">Só voluntários</SelectItem>
            <SelectItem value="donor">Só doadores</SelectItem>
            <SelectItem value="company">Só empresas (PJ)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="surface p-2 sm:p-4 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nome</TableHead>
              <TableHead>Contato</TableHead>
              <TableHead>Tipo</TableHead>
              <TableHead>Cidade / UF</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && items.length === 0 && (
              <TableRow><TableCell colSpan={5} className="py-10 text-center text-[hsl(var(--muted-foreground))]">Carregando…</TableCell></TableRow>
            )}
            {!loading && items.length === 0 && (
              <TableRow><TableCell colSpan={5} className="py-10 text-center text-[hsl(var(--muted-foreground))]">Nenhum apoiador encontrado com esses filtros.</TableCell></TableRow>
            )}
            {items.map((s) => (
              <TableRow key={s.id} data-testid={`supporter-row-${s.id}`}>
                <TableCell>
                  <div className="font-medium">{s.name}</div>
                  {s.company_name && <div className="text-xs text-[hsl(var(--muted-foreground))]">{s.company_name}</div>}
                </TableCell>
                <TableCell className="text-sm">
                  {s.email && <div>{s.email}</div>}
                  {s.phone && <div className="text-[hsl(var(--muted-foreground))] font-mono text-xs">{s.phone}</div>}
                </TableCell>
                <TableCell>
                  <div className="flex flex-col gap-1">
                    {s.kind === "company" && <Badge className="rounded-full bg-sky-100 text-sky-800 border-transparent hover:bg-sky-100 w-fit">Empresa</Badge>}
                    {s.is_volunteer && <Badge className="rounded-full bg-emerald-100 text-emerald-800 border-transparent hover:bg-emerald-100 w-fit">Voluntário</Badge>}
                    {s.is_donor && <Badge className="rounded-full bg-rose-100 text-rose-800 border-transparent hover:bg-rose-100 w-fit">Doador</Badge>}
                  </div>
                </TableCell>
                <TableCell className="text-sm text-[hsl(var(--muted-foreground))]">
                  {s.city ? `${s.city} / ${s.uf || "-"}` : "—"}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button variant="ghost" size="sm" onClick={() => setSelected(s)} className="rounded-full" data-testid={`supporter-view-${s.id}`}><Eye className="h-4 w-4 mr-1" />Ver</Button>
                    {s.phone && (
                      <Button variant="outline" size="sm" onClick={() => openWA(s.phone, s.name)} className="rounded-full text-emerald-700 hover:bg-emerald-50" data-testid={`supporter-wa-${s.id}`}>
                        <MessageCircle className="h-4 w-4 mr-1" />WhatsApp
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="supporter-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{selected?.name}</DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-3 text-sm">
              {selected.company_name && <Row k="Empresa" v={selected.company_name} />}
              {selected.doc_number && <Row k={selected.kind === "company" ? "CNPJ" : "CPF"} v={selected.doc_number} />}
              {selected.email && <Row k="E-mail" v={selected.email} />}
              {selected.phone && <Row k="Telefone" v={selected.phone} />}
              {selected.city && <Row k="Cidade" v={`${selected.city} / ${selected.uf || "-"}`} />}
              {selected.is_volunteer && (
                <Row k="Voluntário — áreas" v={(selected.volunteer_areas || []).join(", ") || "—"} />
              )}
              {selected.volunteer_note && <Row k="Observação" v={selected.volunteer_note} />}
              {selected.is_donor && selected.monthly_pledge && (
                <Row k="Doação mensal declarada" v={`R$ ${selected.monthly_pledge}`} />
              )}
            </div>
          )}
          <DialogFooter>
            {selected?.phone && (
              <Button onClick={() => openWA(selected.phone, selected.name)} className="rounded-full bg-[#25D366] text-white hover:bg-[#25D366]/90">
                <MessageCircle className="h-4 w-4 mr-1.5" /> Chamar no WhatsApp
              </Button>
            )}
            {selected?.email && (
              <a href={`mailto:${selected.email}`} className="btn-pill inline-flex items-center gap-1.5 px-4 py-2 bg-[hsl(var(--primary))] text-white text-sm">
                E-mail
              </a>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="grid grid-cols-[140px_1fr] gap-3 items-baseline">
      <div className="text-[10px] font-bold uppercase tracking-widest text-[hsl(var(--muted-foreground))]">{k}</div>
      <div>{v}</div>
    </div>
  );
}


function SubscriptionsTab() {
  const [metrics, setMetrics] = useState({ mrr: 0, active: 0, past_due: 0, canceled: 0 });
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("active");
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/admin/subscriptions/metrics").then(({ data }) => setMetrics(data)).catch(() => {}),
      api.get("/admin/subscriptions").then(({ data }) => setItems(data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const filtered = items.filter((s) => {
    if (filter === "active") return s.active && !s.canceled_at && !s.past_due;
    if (filter === "past_due") return s.past_due && !s.canceled_at;
    if (filter === "canceled") return !!s.canceled_at;
    return true;
  });

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs font-bold uppercase tracking-widest text-[hsl(var(--secondary))]">Doações mensais recorrentes</div>
        <h2 className="font-heading text-2xl font-black">Painel de assinantes Stripe</h2>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <SubsKpi title="MRR (mensal)" value={`R$ ${metrics.mrr.toFixed(2)}`} sub="Receita recorrente ativa" color="primary" testid="subs-kpi-mrr" />
        <SubsKpi title="Doadores ativos" value={metrics.active} sub="Cobrança em dia" color="primary" testid="subs-kpi-active" />
        <SubsKpi title="Em atraso" value={metrics.past_due} sub="Pagamento pendente" color="warn" testid="subs-kpi-pastdue" />
        <SubsKpi title="Cancelados" value={metrics.canceled} sub="Assinatura encerrada" color="muted" testid="subs-kpi-canceled" />
      </div>

      <div className="surface p-2 sm:p-4 overflow-hidden">
        <div className="flex flex-wrap gap-2 p-2">
          {[
            { k: "active", label: `Ativos (${metrics.active})` },
            { k: "past_due", label: `Em atraso (${metrics.past_due})` },
            { k: "canceled", label: `Cancelados (${metrics.canceled})` },
            { k: "all", label: "Todos" },
          ].map((f) => (
            <button key={f.k} onClick={() => setFilter(f.k)}
              className={`text-sm px-4 h-9 rounded-full border transition-colors ${
                filter === f.k ? "bg-[hsl(var(--primary))] border-[hsl(var(--primary))] text-white" : "bg-white border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))]/50"
              }`} data-testid={`subs-filter-${f.k}`}>
              {f.label}
            </button>
          ))}
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nome</TableHead>
              <TableHead>E-mail</TableHead>
              <TableHead className="text-right">Valor / mês</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Última cobrança</TableHead>
              <TableHead>Início</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={6} className="text-center py-10">Carregando…</TableCell></TableRow>}
            {!loading && filtered.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-10 text-[hsl(var(--muted-foreground))]">Nenhum registro nesta categoria.</TableCell></TableRow>}
            {filtered.map((s) => (
              <TableRow key={s.id} data-testid={`subs-row-${s.id}`}>
                <TableCell className="font-medium">{s.name}</TableCell>
                <TableCell><span className="text-xs">{s.email}</span></TableCell>
                <TableCell className="text-right font-semibold">R$ {Number(s.monthly_amount || 0).toFixed(2)}</TableCell>
                <TableCell>
                  {s.canceled_at ? <Badge variant="outline" className="rounded-full">Cancelado</Badge>
                   : s.past_due ? <Badge className="rounded-full bg-amber-100 text-amber-700 hover:bg-amber-100">Em atraso</Badge>
                   : s.active ? <Badge className="rounded-full bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/15">Ativo</Badge>
                   : <Badge variant="outline">Inativo</Badge>}
                </TableCell>
                <TableCell className="text-xs">{s.last_payment_at ? new Date(s.last_payment_at).toLocaleDateString("pt-BR") : "—"}</TableCell>
                <TableCell className="text-xs">{s.created_at ? new Date(s.created_at).toLocaleDateString("pt-BR") : "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function SubsKpi({ title, value, sub, color = "primary", testid }) {
  const colorClass = color === "warn" ? "bg-amber-100 text-amber-700"
    : color === "muted" ? "bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]"
    : "bg-[hsl(var(--primary))]/12 text-[hsl(var(--primary))]";
  return (
    <div className="surface p-5" data-testid={testid}>
      <div className={`h-10 w-10 rounded-xl grid place-items-center ${colorClass}`}><Heart className="h-4 w-4" /></div>
      <div className="mt-3 text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--muted-foreground))]">{title}</div>
      <div className="font-heading text-2xl font-black">{value}</div>
      <div className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">{sub}</div>
    </div>
  );
}



// ------------------------- Shop tab (Marketplace) -------------------------
const EMPTY_PRODUCT = {
  name: "", description: "", category: "Vestuário",
  price_cents: 0, weight_grams: 300,
  images: [], variants: [{ size: "P", stock: 0 }, { size: "M", stock: 0 }, { size: "G", stock: 0 }, { size: "GG", stock: 0 }],
  published: true,
};

function formatBRLc(cents) {
  const v = (Number(cents) || 0) / 100;
  return v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function ShopTab() {
  const [subTab, setSubTab] = useState("products");
  return (
    <div className="space-y-5" data-testid="admin-shop-root">
      <div>
        <h2 className="font-heading text-2xl font-extrabold">Loja Solidária</h2>
        <p className="text-sm text-[hsl(var(--muted-foreground))]">Gerencie os produtos, os pedidos e a tabela de frete da loja do instituto.</p>
      </div>
      <div className="inline-flex gap-1.5 rounded-full bg-[hsl(var(--muted))] p-1.5">
        {[
          { k: "products", label: "Produtos", Icon: Package },
          { k: "orders", label: "Pedidos", Icon: ClipboardList },
          { k: "shipping", label: "Frete", Icon: FileDown },
        ].map(({ k, label, Icon }) => (
          <button
            key={k}
            onClick={() => setSubTab(k)}
            data-testid={`admin-shop-sub-${k}`}
            className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-medium ${subTab === k ? "bg-white shadow text-[hsl(var(--foreground))]" : "text-[hsl(var(--muted-foreground))]"}`}
          >
            <Icon className="h-4 w-4" /> {label}
          </button>
        ))}
      </div>
      {subTab === "products" && <ShopProductsPanel />}
      {subTab === "orders" && <ShopOrdersPanel />}
      {subTab === "shipping" && <ShopShippingPanel />}
    </div>
  );
}

function ShopProductsPanel() {
  const [items, setItems] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_PRODUCT);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const load = () => api.get("/admin/shop/products").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY_PRODUCT); setDialogOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name || "",
      description: p.description || "",
      category: p.category || "Vestuário",
      price_cents: p.price_cents || 0,
      weight_grams: p.weight_grams || 300,
      images: p.images || [],
      variants: (p.variants && p.variants.length) ? p.variants : EMPTY_PRODUCT.variants,
      published: p.published !== false,
    });
    setDialogOpen(true);
  };

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 6 * 1024 * 1024) {
      toast.error("Imagem muito grande (máx. 6MB).");
      e.target.value = "";
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/shop/upload-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((p) => ({ ...p, images: [...(p.images || []), data.url] }));
      toast.success("Imagem enviada.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha no upload.");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removeImage = (idx) => {
    setForm((p) => ({ ...p, images: p.images.filter((_, i) => i !== idx) }));
  };

  const updateVariant = (idx, field, value) => {
    setForm((p) => ({
      ...p,
      variants: p.variants.map((v, i) => i === idx ? { ...v, [field]: field === "stock" ? Math.max(0, parseInt(value || 0, 10)) : value } : v),
    }));
  };

  const addVariant = () => setForm((p) => ({ ...p, variants: [...p.variants, { size: "", stock: 0 }] }));
  const removeVariant = (idx) => setForm((p) => ({ ...p, variants: p.variants.filter((_, i) => i !== idx) }));

  const save = async () => {
    if (!form.name || form.price_cents <= 0) {
      toast.error("Preencha nome e preço.");
      return;
    }
    const payload = {
      ...form,
      price_cents: parseInt(form.price_cents, 10),
      weight_grams: parseInt(form.weight_grams || 300, 10),
      variants: form.variants.filter((v) => (v.size || "").trim().length > 0),
    };
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/admin/shop/products/${editing.id}`, payload);
        toast.success("Produto atualizado.");
      } else {
        await api.post("/admin/shop/products", payload);
        toast.success("Produto criado.");
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível salvar.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir este produto?")) return;
    try {
      await api.delete(`/admin/shop/products/${id}`);
      toast.success("Produto excluído.");
      load();
    } catch { toast.error("Não foi possível excluir."); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-sm text-[hsl(var(--muted-foreground))]">{items.length} produto(s)</div>
        <Button onClick={openCreate} className="btn-pill bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90" data-testid="admin-shop-new-btn">
          <Plus className="mr-1.5 h-4 w-4" /> Novo produto
        </Button>
      </div>

      <div className="surface overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Produto</TableHead>
              <TableHead className="hidden md:table-cell">Categoria</TableHead>
              <TableHead className="hidden md:table-cell">Preço</TableHead>
              <TableHead className="hidden md:table-cell">Estoque</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((p) => (
              <TableRow key={p.id} data-testid={`admin-shop-row-${p.id}`}>
                <TableCell>
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg overflow-hidden bg-[hsl(var(--muted))]">
                      {(p.images || [])[0] ? <img src={p.images[0]} alt="" className="h-full w-full object-cover" /> : null}
                    </div>
                    <div>
                      <div className="font-medium line-clamp-1">{p.name}</div>
                      <div className="text-xs text-[hsl(var(--muted-foreground))]">/{p.slug}</div>
                    </div>
                  </div>
                </TableCell>
                <TableCell className="hidden md:table-cell"><Badge variant="secondary" className="rounded-full">{p.category}</Badge></TableCell>
                <TableCell className="hidden md:table-cell font-semibold">{formatBRLc(p.price_cents)}</TableCell>
                <TableCell className="hidden md:table-cell">
                  {p.sold_out
                    ? <Badge variant="outline" className="rounded-full text-[hsl(var(--destructive))] border-[hsl(var(--destructive))]/40">Esgotado</Badge>
                    : <Badge className="rounded-full bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))] border-transparent">{p.total_stock} unid.</Badge>}
                </TableCell>
                <TableCell className="text-right">
                  <div className="inline-flex gap-1.5">
                    <Button size="sm" variant="outline" className="rounded-full" onClick={() => openEdit(p)} data-testid={`admin-shop-edit-${p.id}`}><Pencil className="h-4 w-4" /></Button>
                    <Button size="sm" variant="outline" className="rounded-full text-[hsl(var(--destructive))] border-[hsl(var(--destructive))]/40 hover:bg-[hsl(var(--destructive))]/10" onClick={() => remove(p.id)} data-testid={`admin-shop-delete-${p.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && (
              <TableRow><TableCell colSpan={5} className="text-center py-10 text-[hsl(var(--muted-foreground))]">Nenhum produto cadastrado.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Editar produto" : "Novo produto"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <Label>Nome</Label>
                <Input className="mt-1.5 rounded-xl h-11" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="admin-shop-name-input" />
              </div>
              <div>
                <Label>Categoria</Label>
                <Input className="mt-1.5 rounded-xl h-11" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="admin-shop-category-input" />
              </div>
              <div>
                <Label>Preço (em centavos, ex.: 8990 = R$ 89,90)</Label>
                <Input type="number" min="0" className="mt-1.5 rounded-xl h-11" value={form.price_cents} onChange={(e) => setForm({ ...form, price_cents: e.target.value })} data-testid="admin-shop-price-input" />
              </div>
              <div>
                <Label>Peso (gramas — usado no frete)</Label>
                <Input type="number" min="1" className="mt-1.5 rounded-xl h-11" value={form.weight_grams} onChange={(e) => setForm({ ...form, weight_grams: e.target.value })} data-testid="admin-shop-weight-input" />
              </div>
              <div className="flex items-end">
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={!!form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} data-testid="admin-shop-published-input" />
                  <span className="text-sm">Publicado na loja</span>
                </label>
              </div>
            </div>

            <div>
              <Label>Descrição</Label>
              <Textarea rows={4} className="mt-1.5 rounded-xl" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="admin-shop-description-input" />
            </div>

            {/* Images */}
            <div>
              <Label>Imagens</Label>
              <div className="mt-1.5 flex flex-wrap gap-2">
                {(form.images || []).map((url, idx) => (
                  <div key={idx} className="relative w-20 h-20 rounded-xl overflow-hidden border border-[hsl(var(--border))]">
                    <img src={url} alt="" className="w-full h-full object-cover" />
                    <button type="button" onClick={() => removeImage(idx)} className="absolute top-1 right-1 h-5 w-5 rounded-full bg-white/95 text-[hsl(var(--destructive))] grid place-items-center shadow" data-testid={`admin-shop-image-remove-${idx}`}>
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                ))}
                <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={uploadImage} data-testid="admin-shop-image-file-input" />
                <button type="button" onClick={() => fileInputRef.current?.click()} disabled={uploading} className="w-20 h-20 rounded-xl border-2 border-dashed border-[hsl(var(--border))] hover:border-[hsl(var(--primary))] grid place-items-center text-[hsl(var(--muted-foreground))]" data-testid="admin-shop-image-upload-btn">
                  {uploading ? <span className="text-xs">…</span> : <Upload className="h-5 w-5" />}
                </button>
              </div>
              <div className="mt-1 text-xs text-[hsl(var(--muted-foreground))]">JPG, PNG ou WEBP até 6MB. A primeira imagem é a capa.</div>
            </div>

            {/* Variants */}
            <div>
              <Label>Variantes (tamanho e estoque)</Label>
              <div className="mt-1.5 space-y-2">
                {form.variants.map((v, idx) => (
                  <div key={idx} className="flex items-center gap-2" data-testid={`admin-shop-variant-${idx}`}>
                    <Input className="rounded-xl h-10 max-w-[100px]" placeholder="P, M, G…" value={v.size} onChange={(e) => updateVariant(idx, "size", e.target.value)} data-testid={`admin-shop-variant-size-${idx}`} />
                    <Input type="number" min="0" className="rounded-xl h-10 max-w-[140px]" placeholder="Estoque" value={v.stock} onChange={(e) => updateVariant(idx, "stock", e.target.value)} data-testid={`admin-shop-variant-stock-${idx}`} />
                    <Button type="button" size="sm" variant="outline" className="rounded-full" onClick={() => removeVariant(idx)} data-testid={`admin-shop-variant-remove-${idx}`}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                ))}
                <Button type="button" size="sm" variant="outline" className="rounded-full" onClick={addVariant} data-testid="admin-shop-variant-add"><Plus className="h-4 w-4 mr-1" /> Adicionar tamanho</Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={save} disabled={saving} className="bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90" data-testid="admin-shop-save-btn">
              {saving ? "Salvando…" : (editing ? "Salvar" : "Criar produto")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ShopOrdersPanel() {
  const [orders, setOrders] = useState([]);
  useEffect(() => { api.get("/admin/shop/orders").then(({ data }) => setOrders(data)).catch(() => {}); }, []);
  return (
    <div className="surface overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Pedido</TableHead>
            <TableHead>Cliente</TableHead>
            <TableHead className="hidden md:table-cell">Itens</TableHead>
            <TableHead className="hidden md:table-cell">Total</TableHead>
            <TableHead className="hidden md:table-cell">Status</TableHead>
            <TableHead className="hidden lg:table-cell">Data</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {orders.map((o) => (
            <TableRow key={o.id} data-testid={`admin-order-row-${o.id}`}>
              <TableCell className="text-xs font-mono">{o.session_id?.slice(-10)}</TableCell>
              <TableCell>
                <div className="font-medium">{o.buyer_name}</div>
                <div className="text-xs text-[hsl(var(--muted-foreground))]">{o.buyer_email}</div>
              </TableCell>
              <TableCell className="hidden md:table-cell text-sm">{(o.items || []).length}</TableCell>
              <TableCell className="hidden md:table-cell font-semibold">{formatBRLc(o.total_cents)}</TableCell>
              <TableCell className="hidden md:table-cell">
                {o.payment_status === "paid"
                  ? <Badge className="rounded-full bg-emerald-100 text-emerald-800 border-transparent">Pago</Badge>
                  : <Badge variant="outline" className="rounded-full">{o.payment_status || "pendente"}</Badge>}
              </TableCell>
              <TableCell className="hidden lg:table-cell text-xs text-[hsl(var(--muted-foreground))]">
                {o.created_at ? new Date(o.created_at).toLocaleString("pt-BR") : ""}
              </TableCell>
            </TableRow>
          ))}
          {orders.length === 0 && (
            <TableRow><TableCell colSpan={6} className="text-center py-10 text-[hsl(var(--muted-foreground))]">Nenhum pedido ainda.</TableCell></TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}

function ShopShippingPanel() {
  const [tiers, setTiers] = useState([]);
  const [freeOver, setFreeOver] = useState(0);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/shop/shipping").then(({ data }) => {
    setTiers(data.tiers || []);
    setFreeOver(data.free_over_cents || 0);
  });
  useEffect(() => { load(); }, []);

  const updateTier = (idx, field, value) => {
    setTiers((prev) => prev.map((t, i) => i === idx ? { ...t, [field]: parseInt(value || 0, 10) } : t));
  };
  const addTier = () => setTiers((p) => [...p, { max_weight_g: 1000, price_cents: 2000 }]);
  const removeTier = (idx) => setTiers((p) => p.filter((_, i) => i !== idx));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/shop/shipping", { tiers, free_over_cents: parseInt(freeOver || 0, 10) });
      toast.success("Tabela de frete salva.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao salvar.");
    } finally { setSaving(false); }
  };

  return (
    <div className="surface p-6 space-y-5">
      <div>
        <h3 className="font-heading font-extrabold text-lg">Tabela de frete por peso</h3>
        <p className="text-sm text-[hsl(var(--muted-foreground))]">O frete é calculado somando o peso de todos os itens do carrinho e aplicando a primeira faixa que couber.</p>
      </div>
      <div className="space-y-2">
        {tiers.map((t, idx) => (
          <div key={idx} className="flex items-center gap-2" data-testid={`admin-shipping-tier-${idx}`}>
            <div className="text-sm text-[hsl(var(--muted-foreground))]">Até</div>
            <Input type="number" min="1" className="rounded-xl h-10 max-w-[140px]" value={t.max_weight_g} onChange={(e) => updateTier(idx, "max_weight_g", e.target.value)} />
            <div className="text-sm">g →</div>
            <Input type="number" min="0" className="rounded-xl h-10 max-w-[140px]" value={t.price_cents} onChange={(e) => updateTier(idx, "price_cents", e.target.value)} />
            <div className="text-sm text-[hsl(var(--muted-foreground))]">centavos ({formatBRLc(t.price_cents)})</div>
            <Button size="sm" variant="outline" className="rounded-full ml-auto" onClick={() => removeTier(idx)}><Trash2 className="h-4 w-4" /></Button>
          </div>
        ))}
        <Button size="sm" variant="outline" className="rounded-full" onClick={addTier} data-testid="admin-shipping-add-tier"><Plus className="h-4 w-4 mr-1" /> Nova faixa</Button>
      </div>
      <div>
        <Label>Frete grátis acima de (centavos, 0 = desligado)</Label>
        <Input type="number" min="0" className="mt-1.5 rounded-xl h-11 max-w-[220px]" value={freeOver} onChange={(e) => setFreeOver(e.target.value)} data-testid="admin-shipping-free-over" />
        <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1">Ex.: 20000 → frete grátis para carrinhos ≥ R$ 200,00.</div>
      </div>
      <Button onClick={save} disabled={saving} className="bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90" data-testid="admin-shipping-save-btn">
        {saving ? "Salvando…" : "Salvar tabela"}
      </Button>
    </div>
  );
}

// ------------------------- Partners tab -------------------------
const EMPTY_PARTNER = { name: "", tier: "Apoiador", logo_url: "", website: "", description: "", published: true };

function PartnersTab() {
  const [items, setItems] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_PARTNER);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/partners").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY_PARTNER); setDialogOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name || "",
      tier: p.tier || "Apoiador",
      logo_url: p.logo_url || "",
      website: p.website || "",
      description: p.description || "",
      published: p.published !== false,
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.name || form.name.trim().length < 2) {
      toast.error("Informe o nome do parceiro.");
      return;
    }
    const payload = {
      ...form,
      logo_url: form.logo_url || null,
      website: form.website || null,
    };
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/admin/partners/${editing.id}`, payload);
        toast.success("Parceiro atualizado.");
      } else {
        await api.post("/admin/partners", payload);
        toast.success("Parceiro cadastrado. Aviso publicado na comunidade!");
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível salvar.");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir este parceiro?")) return;
    try {
      await api.delete(`/admin/partners/${id}`);
      toast.success("Parceiro excluído.");
      load();
    } catch { toast.error("Não foi possível excluir."); }
  };

  return (
    <div className="space-y-4" data-testid="admin-partners-root">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl font-extrabold">Parceiros & Patrocinadores</h2>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">Cada novo parceiro publicado dispara um aviso na timeline da comunidade.</p>
        </div>
        <Button onClick={openCreate} className="btn-pill bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90" data-testid="admin-partners-new-btn">
          <Plus className="mr-1.5 h-4 w-4" /> Novo parceiro
        </Button>
      </div>

      <div className="surface overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nome</TableHead>
              <TableHead className="hidden md:table-cell">Nível</TableHead>
              <TableHead className="hidden md:table-cell">Site</TableHead>
              <TableHead className="hidden md:table-cell">Status</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((p) => (
              <TableRow key={p.id} data-testid={`admin-partner-row-${p.id}`}>
                <TableCell>
                  <div className="flex items-center gap-3">
                    {p.logo_url ? <img src={p.logo_url} alt="" className="h-9 w-9 rounded-lg object-cover" /> : <div className="h-9 w-9 rounded-lg bg-[hsl(var(--muted))] grid place-items-center"><Handshake className="h-4 w-4 text-[hsl(var(--muted-foreground))]" /></div>}
                    <div>
                      <div className="font-medium">{p.name}</div>
                      <div className="text-xs text-[hsl(var(--muted-foreground))] line-clamp-1">{p.description}</div>
                    </div>
                  </div>
                </TableCell>
                <TableCell className="hidden md:table-cell"><Badge variant="secondary" className="rounded-full">{p.tier}</Badge></TableCell>
                <TableCell className="hidden md:table-cell text-xs">
                  {p.website ? <a href={p.website} target="_blank" rel="noreferrer" className="text-[hsl(var(--primary))] hover:underline">Abrir</a> : "—"}
                </TableCell>
                <TableCell className="hidden md:table-cell">
                  {p.published
                    ? <Badge className="rounded-full bg-emerald-100 text-emerald-800 border-transparent">Publicado</Badge>
                    : <Badge variant="outline" className="rounded-full">Oculto</Badge>}
                </TableCell>
                <TableCell className="text-right">
                  <div className="inline-flex gap-1.5">
                    <Button size="sm" variant="outline" className="rounded-full" onClick={() => openEdit(p)} data-testid={`admin-partner-edit-${p.id}`}><Pencil className="h-4 w-4" /></Button>
                    <Button size="sm" variant="outline" className="rounded-full text-[hsl(var(--destructive))] border-[hsl(var(--destructive))]/40 hover:bg-[hsl(var(--destructive))]/10" onClick={() => remove(p.id)} data-testid={`admin-partner-delete-${p.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && (
              <TableRow><TableCell colSpan={5} className="text-center py-10 text-[hsl(var(--muted-foreground))]">Nenhum parceiro cadastrado.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Editar parceiro" : "Novo parceiro"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Nome</Label>
              <Input className="mt-1.5 rounded-xl h-11" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="admin-partner-name" />
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label>Nível</Label>
                <Input className="mt-1.5 rounded-xl h-11" placeholder="Ouro, Prata, Apoiador…" value={form.tier} onChange={(e) => setForm({ ...form, tier: e.target.value })} data-testid="admin-partner-tier" />
              </div>
              <div>
                <Label>Site (opcional)</Label>
                <Input className="mt-1.5 rounded-xl h-11" placeholder="https://…" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} data-testid="admin-partner-website" />
              </div>
            </div>
            <div>
              <Label>URL do logo (opcional)</Label>
              <Input className="mt-1.5 rounded-xl h-11" placeholder="https://…" value={form.logo_url} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} data-testid="admin-partner-logo" />
            </div>
            <div>
              <Label>Descrição / mensagem</Label>
              <Textarea rows={3} className="mt-1.5 rounded-xl" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="admin-partner-description" />
            </div>
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={!!form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} data-testid="admin-partner-published" />
              <span className="text-sm">Publicado (dispara aviso na comunidade)</span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={save} disabled={saving} className="bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90" data-testid="admin-partner-save">
              {saving ? "Salvando…" : (editing ? "Salvar" : "Cadastrar")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

